import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Phone, MessageSquare, Clock, Car, Trash2, Send, Mail, ChevronDown, Calendar, AlertCircle, Eye, CheckCircle, CreditCard, Activity, ChevronUp, Copy, Package2, Link, Search, FileText, GripVertical, Pencil, Users, ArrowLeft, Receipt, MapPin, Truck, Folder } from 'lucide-react';
import { Proposal, ProposalOffer, approveProposalOffer, declineProposalOffer, getProposal, deleteProposal, getProposalEvents, sendProposalLinkViaBoth, getOrCreateProposalToken, buildProposalCustomerLink, logProposalEvent, generateProposalEmailHTML, generateProposalPDF } from '../lib/proposals';
import { supabase } from '../lib/supabaseClient';
import { useModalZIndex } from '../contexts/ModalStackContext';
import CreateBookingModal from './CreateBookingModal';
import CreateMobileBookingModal from './CreateMobileBookingModal';
import { createTestProposalEventSimple } from '../lib/debugUtils';
import RichTextDescription from './RichTextDescription';
import ProposalEmailModal from './ProposalEmailModal';
import ProposalSMSModal from './ProposalSMSModal';
import { sendEmailWithFallback } from '../lib/email';
import { getProposalPhotos, ProposalPhoto } from '../services/proposalPhotoService';
import {
  listProposalServicePhotos,
  deleteProposalServicePhoto,
  groupPhotosByLineKey,
  proposalServicePhotoUrl,
  type ProposalServicePhoto,
} from '../services/proposalServicePhotoService';
import { useImageViewer } from './ImageViewerProvider';
import { sendSMS } from '../lib/messaging';
import { ProductPhotoService } from '../services/productPhotoService';
import CustomerDetailsModal from './CustomerDetailsModal';
import { getCustomer, Customer } from '../lib/customers';
import ConvertModal from './ConvertModal';
import RecordPaymentModal from './RecordPaymentModal';
import { useBusiness } from '../contexts/BusinessContext';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import CreateProposalModal from './CreateProposalModal';

// Lazy load CustomerMessageModal to avoid circular dependency
const CustomerMessageModal = lazy(() => import('./CustomerMessageModal'));
import { useAppSystem } from '../hooks/useAppSystem';
import { addProjectService } from '../lib/projectServices';
import toast from 'react-hot-toast';
import { InvoiceSettings, InvoiceSettingsService, defaultInvoiceSettings } from '../services/invoiceSettingsService';

interface ProposalDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposal: Proposal;
  onUpdate?: (proposal: Proposal) => void;
  readOnly?: boolean;
  onDelete?: () => void;
}

export interface ProposalService {
  id: string;
  proposal_id: string;
  offer_id: string;
  service_id: string;
  vehicle_id: string;
  name: string;
  description: string | null;
  price: number; // This is the original price
  discount_type?: 'percentage' | 'dollar';
  discount_value?: number;
  discount_amount: number;
  tax_exempt: boolean;
  tax_rate: number | null;
  tax_amount: number;
  total_amount: number;
  duration_minutes: number;
  order_index: number;
  created_at: string;
  updated_at: string;
  vehicle_make?: string;
  vehicle_model?: string;
  is_selected?: boolean;
  isSelectable?: boolean;
  // Legacy fields for backward compatibility
  originalPrice?: number;
  discountType?: 'percentage' | 'dollar';
  discountValue?: number;
}

export interface ProposalEvent {
  id: string;
  proposal_id: string;
  business_id: string;
  event_type: string;
  message: string | null;
  metadata: Record<string, any> | null;
  event_timestamp: string;
  created_by: string | null;
}

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'draft':
      return {
        color: 'bg-gray-500',
        text: 'Draft',
        description: 'Not yet sent to customer'
      };
    case 'held':
      return {
        color: 'bg-gray-500',
        text: 'Not sent',
        description: 'Goes out when you send it from the lead'
      };
    case 'sent':
      return {
        color: 'bg-blue-500',
        text: 'Sent',
        description: 'Awaiting customer review'
      };
    case 'viewed':
      return {
        color: 'bg-purple-500',
        text: 'Viewed',
        description: 'Customer has viewed the proposal'
      };
    case 'approved':
      return {
        color: 'bg-green-500',
        text: 'Approved',
        description: 'Customer has approved an offer'
      };
    case 'declined':
      return {
        color: 'bg-red-500',
        text: 'Declined',
        description: 'Customer has declined all offers'
      };
    default:
      return {
        color: 'bg-gray-500',
        text: 'Unknown',
        description: ''
      };
  }
};

export default function ProposalDetailsModal({ 
  isOpen, 
  onClose,
  proposal,
  onUpdate,
  readOnly = false,
  onDelete
}: ProposalDetailsModalProps) {
  const navigate = useNavigate();
  const { coreApps, installedAddons } = useAppSystem();
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConvertModalOpen, setIsConvertModalOpen] = useState(false);
  const [isConvertMobileModalOpen, setIsConvertMobileModalOpen] = useState(false);
  // Auto-elevate above a parent modal (e.g. CustomerMessageModal's milestone view)
  // via the modal stack; standalone keeps the original z-index.
  const resolvedZIndex = useModalZIndex(100130);
  const [isConvertOptionsModalOpen, setIsConvertOptionsModalOpen] = useState(false);
  const [showLocationBooking, setShowLocationBooking] = useState(true);
  const [showMobileBooking, setShowMobileBooking] = useState(true);
  const [localProposal, setLocalProposal] = useState<Proposal>(proposal);
  const { canAccess } = usePermissionCheck();
  
  // Check if apps are installed
  const allApps = [...coreApps, ...installedAddons];
  const hasCalendarApp = allApps.some(app => app.app?.slug === 'calendar' && app.status === 'active');
  const hasProjectsApp = allApps.some(app => app.app?.slug === 'projects' && app.status === 'active');
  const hasCRMApp = allApps.some(app => app.app?.slug === 'crm' && app.status === 'active');
  const [isSending, setIsSending] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [proposalEvents, setProposalEvents] = useState<ProposalEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [justSent, setJustSent] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isSMSModalOpen, setIsSMSModalOpen] = useState(false);
  const [businessName, setBusinessName] = useState('your service provider');
  const [urlCopied, setUrlCopied] = useState(false);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [proposalPhotos, setProposalPhotos] = useState<ProposalPhoto[]>([]);
  const [servicePhotosByKey, setServicePhotosByKey] = useState<Record<string, ProposalServicePhoto[]>>({});
  // Cache-bust tokens (per photo id) so an edited image refetches instead of
  // showing the browser-cached copy at the same URL.
  const [photoBust, setPhotoBust] = useState<Record<string, number>>({});
  // Fallback map of proposal_services.id -> line_key, read straight from the base
  // table so per-service photos still match even if the details view doesn't
  // surface line_key (stale PostgREST cache / unapplied view migration).
  const [lineKeyByServiceId, setLineKeyByServiceId] = useState<Record<string, string>>({});
  const { open: openImageViewer } = useImageViewer();
  const [isCustomerDetailsModalOpen, setIsCustomerDetailsModalOpen] = useState(false);
  const [fullCustomer, setFullCustomer] = useState<Customer | null>(null);
  const [project, setProject] = useState<{ id: string; title: string } | null>(null);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [linkedBooking, setLinkedBooking] = useState<{ id: string; title: string; booking_number: number; status: string; mobile_booking?: boolean } | null>(null);
  const [isLoadingBooking, setIsLoadingBooking] = useState(false);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);
  const [messageModalComposerType, setMessageModalComposerType] = useState<'sms' | 'email'>('sms');
  const [expandedOffers, setExpandedOffers] = useState<Set<string>>(new Set());
  const [isConvertingToProject, setIsConvertingToProject] = useState(false);
  const [draggedOfferIndex, setDraggedOfferIndex] = useState<number | null>(null);
  
  // Link modal state
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkModalView, setLinkModalView] = useState<'options' | 'projects' | 'opportunities' | 'bookings'>('options');
  const [projects, setProjects] = useState<Array<{ id: string; title: string; status: string; customer_id: string | null; customer?: { id: string; first_name: string; last_name: string } | null }>>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [opportunities, setOpportunities] = useState<Array<{ id: number; title: string; customer_id: string | null; status: string; value: number; customer?: { id: string; first_name: string; last_name: string } | null }>>([]);
  const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);
  const [opportunitySearchQuery, setOpportunitySearchQuery] = useState('');
  const [bookingsForLink, setBookingsForLink] = useState<Array<{ id: string; title: string; booking_number: number; status: string; customer?: { id: string; first_name: string; last_name: string } | null }>>([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [bookingSearchQuery, setBookingSearchQuery] = useState('');
  const [opportunity, setOpportunity] = useState<{ id: number; title: string } | null>(null);
  const [isLoadingOpportunity, setIsLoadingOpportunity] = useState(false);
  
  // Payment state
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [proposalAmountPaid, setProposalAmountPaid] = useState(0);
  const [proposalPayments, setProposalPayments] = useState<any[]>([]);
  const [calculatedDepositAmount, setCalculatedDepositAmount] = useState<number | null>(null);
  const { selectedBusiness } = useBusiness();
  const [hasTwilioConnected, setHasTwilioConnected] = useState(false);
  const [hasGmailConnected, setHasGmailConnected] = useState(false);
  const [hasSendGridMarketing, setHasSendGridMarketing] = useState(false);
  const [isEditProposalModalOpen, setIsEditProposalModalOpen] = useState(false);
  
  const [businessTaxRates, setBusinessTaxRates] = useState<{ name: string; rate: number }[]>([]);

  useEffect(() => {
    if (selectedBusiness?.id) {
      supabase
        .from('tax_rates')
        .select('name, rate')
        .eq('business_id', selectedBusiness.id)
        .then(({ data }) => { if (data) setBusinessTaxRates(data); });
    }
  }, [selectedBusiness?.id]);

  const combinedBusinessRate = businessTaxRates.reduce((sum, r) => sum + r.rate, 0) / 100;

  const [serviceAdditionalTaxes, setServiceAdditionalTaxes] = useState<{ service_id: string; name: string; rate: number }[]>([]);

  useEffect(() => {
    const allServiceIds = localProposal?.offers
      ?.flatMap(o => (o.services || []).map((s: any) => s.service_id))
      .filter(Boolean) || [];
    const uniqueIds = [...new Set(allServiceIds)];
    if (uniqueIds.length === 0) { setServiceAdditionalTaxes([]); return; }
    supabase
      .from('service_additional_taxes')
      .select('service_id, name, rate')
      .in('service_id', uniqueIds)
      .then(({ data }) => { if (data) setServiceAdditionalTaxes(data); });
  }, [localProposal?.offers]);

  // View state for switching between Details and Invoice
  const [activeView, setActiveView] = useState<'details' | 'invoice'>('details');
  const [invoiceSettings, setInvoiceSettings] = useState<InvoiceSettings | null>(null);
  const [isLoadingInvoiceSettings, setIsLoadingInvoiceSettings] = useState(false);
  
  // Calculate deposit amount from approved offer's deposit percentage
  const calculateDepositFromPercentage = () => {
    if (!localProposal?.offers || localProposal.status !== 'approved') {
      return null;
    }
    
    // Find approved offer
    const approvedOffer = localProposal.offers.find(o => o.status === 'approved');
    if (!approvedOffer || !approvedOffer.deposit_percentage || approvedOffer.deposit_percentage <= 0) {
      return null;
    }
    
    // Calculate final total from selected services (including optional services that are selected)
    const selectedServices = approvedOffer.services?.filter(service => {
      const isSelectable = service.isSelectable || false;
      const isSelected = (service as any).is_selected === true;
      return !isSelectable || (isSelectable && isSelected);
    }) || [];
    
    // Calculate service total (with discounts)
    const serviceTotal = selectedServices.reduce((sum, service) => {
      const price = service.price || 0;
      const discountAmount = (service as any).discount_amount || 0;
      const finalPrice = price - discountAmount;
      const taxAmount = service.tax_exempt ? 0 : ((service as any).tax_amount || 0);
      return sum + finalPrice + taxAmount;
    }, 0);
    
    // Calculate product total if products exist
    const products = (approvedOffer as any).products || [];
    const productTotal = products.reduce((sum: number, product: any) => {
      const price = product.price || 0;
      const quantity = product.quantity || 1;
      return sum + (price * quantity);
    }, 0);
    
    const finalTotal = serviceTotal + productTotal;
    
    // Calculate deposit amount from percentage
    const depositAmount = Math.round((finalTotal * approvedOffer.deposit_percentage / 100) * 100) / 100;
    
    return depositAmount;
  };
  
  // Function to convert proposal services to booking services
  const handleConvertToBooking = async () => {
    const bookingId = (localProposal as any).booking_id;
    if (!bookingId) {
      toast.error('Proposal must be linked to a booking to convert.');
      return;
    }

    const approvedOffer = localProposal.offers?.find(offer => offer.status === 'approved');
    if (!approvedOffer) {
      toast.error('No approved offer found. Please approve an offer first.');
      return;
    }

    const approvedServices = approvedOffer.services || [];
    if (approvedServices.length === 0) {
      toast.error('The approved offer has no services to convert.');
      return;
    }

    try {
      setIsConvertingToProject(true);
      setError(null);

      // Get the booking to find its business_id and existing vehicle
      const { data: bookingData, error: bookingFetchError } = await supabase
        .from('bookings')
        .select('id, business_id, customer_id')
        .eq('id', bookingId)
        .single();

      if (bookingFetchError || !bookingData) {
        toast.error('Failed to load booking details.');
        return;
      }

      let addedCount = 0;
      for (const service of approvedServices) {
        const isSelectable = (service as any).is_selectable || service.isSelectable || false;
        const isSelected = (service as any).is_selected === true;
        if (isSelectable && !isSelected) continue;

        const pricingType = (service as any).pricing_type || 'flat';
        const isPerUnit = pricingType === 'per_unit' || pricingType === 'per_sqft';

        const taxRate = service.tax_rate || (service.tax_exempt ? 0 : 5);
        const taxAmount = service.tax_exempt ? 0 : Math.round((service.price * taxRate / 100) * 100) / 100;
        const totalAmount = Math.round((service.price + taxAmount) * 100) / 100;

        const { error: insertError } = await supabase
          .from('booking_services')
          .insert({
            business_id: bookingData.business_id,
            booking_id: bookingId,
            service_id: service.service_id,
            vehicle_id: service.vehicle_id || null,
            name: service.name,
            description: service.description || '',
            price: service.price,
            tax_exempt: service.tax_exempt || false,
            tax_rate: taxRate,
            tax_amount: taxAmount,
            total_amount: totalAmount,
            duration_minutes: service.duration_minutes,
            status: 'pending',
            pricing_type: pricingType,
            unit_price: isPerUnit ? ((service as any).unit_price || 0) : null,
            duration_per_unit: isPerUnit ? ((service as any).duration_per_unit || 0) : null,
            quantity: (service as any).quantity || (isPerUnit ? 0 : 1),
            compare_at_price: (service as any).compare_at_price ?? null
          });

        if (insertError) {
          console.error('Error inserting booking service:', insertError);
        } else {
          addedCount++;
        }
      }

      // Update proposal status to 'booked'
      const { error: proposalUpdateError } = await supabase
        .from('proposals')
        .update({ status: 'booked' })
        .eq('id', localProposal.id);

      if (proposalUpdateError) {
        console.error('Error updating proposal status:', proposalUpdateError);
      }

      // Merge the proposal's standalone deposit invoice into the existing
      // booking's invoice: carries any deposit over and removes the now-empty
      // proposal invoice. No-ops when no deposit invoice exists.
      try {
        const { error: mergeError } = await supabase.rpc('merge_proposal_into_booking', {
          p_proposal_id: localProposal.id,
          p_booking_id: bookingId,
        });
        if (mergeError) {
          console.error('Error merging proposal invoice into booking:', mergeError);
        }
      } catch (mergeErr) {
        console.error('Error merging proposal invoice into booking:', mergeErr);
      }

      toast.success(`Successfully added ${addedCount} service(s) to the booking.`);

      // Dispatch event to notify BookingInfoModal to refresh
      window.dispatchEvent(new CustomEvent('bookingServicesUpdated', {
        detail: { bookingId }
      }));

      if (onUpdate && localProposal.id) {
        const updatedProposal = await getProposal(localProposal.id);
        onUpdate(updatedProposal);
        setLocalProposal(updatedProposal);
      }

      setIsConvertOptionsModalOpen(false);
    } catch (err) {
      console.error('Error converting proposal to booking services:', err);
      toast.error('Failed to convert proposal to booking services.');
    } finally {
      setIsConvertingToProject(false);
    }
  };

  // Function to convert proposal services to project services
  const handleConvertToProject = async () => {
    if (!localProposal.project_id) {
      toast.error('Proposal must be linked to a project to convert.');
      return;
    }

    // Find the approved offer
    const approvedOffer = localProposal.offers?.find(offer => offer.status === 'approved');
    
    if (!approvedOffer) {
      toast.error('No approved offer found. Please approve an offer first.');
      return;
    }

    // Get services from the approved offer
    const approvedServices = approvedOffer.services || [];
    
    if (approvedServices.length === 0) {
      toast.error('The approved offer has no services to convert.');
      return;
    }

    try {
      setIsConvertingToProject(true);
      setError(null);

      // Add each service to the project
      for (const service of approvedServices) {
        const isSelectable = (service as any).is_selectable || service.isSelectable || false;
        const isSelected = (service as any).is_selected === true;
        
        if (isSelectable && !isSelected) {
          continue;
        }

        await addProjectService({
          project_id: localProposal.project_id,
          service_id: service.service_id,
          vehicle_id: service.vehicle_id || null,
          name: service.name,
          description: service.description || null,
          price: service.price,
          tax_exempt: service.tax_exempt || false,
          tax_rate: service.tax_rate || null,
          tax_amount: service.tax_amount || 0,
          discount_amount: service.discount_amount || 0,
          total_amount: service.total_amount || service.price,
          duration_minutes: service.duration_minutes,
          notes: `Converted from proposal: ${localProposal.title}`,
        });
      }

      // Update proposal status to 'booked' after converting to project
      try {
        const { error: proposalUpdateError } = await supabase
          .from('proposals')
          .update({ status: 'booked' })
          .eq('id', localProposal.id);

        if (proposalUpdateError) {
          console.error('❌ Failed to update proposal status to booked:', proposalUpdateError);
        } else {
          console.log('✅ Updated proposal status to booked:', localProposal.id);
        }
      } catch (proposalError) {
        console.error('❌ Error updating proposal status:', proposalError);
        // Don't throw here - the services were already added successfully
      }

      // Apply proposal payments to the project
      if (localProposal.project_id) {
        console.log('🔄 Applying proposal payments to project:', {
          proposalId: localProposal.id,
          projectId: localProposal.project_id
        });
        
        // Find all payments for this proposal
        const { data: proposalPayments, error: paymentsError } = await supabase
          .from('payments')
          .select('*')
          .eq('proposal_id', localProposal.id);
        
        if (paymentsError) {
          console.error('❌ Error fetching proposal payments:', paymentsError);
        } else if (proposalPayments && proposalPayments.length > 0) {
          console.log(`📦 Found ${proposalPayments.length} payment(s) to link to project ${localProposal.project_id}`);
          
          // Update each payment to link it to the project
          for (const payment of proposalPayments) {
            console.log(`🔄 Processing payment ${payment.id}:`, {
              current_project_id: payment.project_id,
              current_proposal_id: payment.proposal_id,
              payment_type: payment.payment_type,
              status: payment.status,
              amount: payment.amount
            });
            
            const updateData: any = { 
              project_id: localProposal.project_id,
            };
            
            // Preserve payment_type if it exists
            if (payment.payment_type) {
              updateData.payment_type = payment.payment_type;
            }
            
            const { error: updatePaymentError, data: updatedPayment } = await supabase
              .from('payments')
              .update(updateData)
              .eq('id', payment.id)
              .select()
              .single();
            
            if (updatePaymentError) {
              console.error(`❌ Error updating payment ${payment.id}:`, updatePaymentError);
            } else if (!updatedPayment) {
              console.error(`❌ Update returned no data for payment ${payment.id}`);
              // Verify by querying the payment again
              const { data: verifyPayment, error: verifyError } = await supabase
                .from('payments')
                .select('project_id, proposal_id, payment_type, status')
                .eq('id', payment.id)
                .single();
              
              if (verifyError) {
                console.error(`❌ Could not verify payment update:`, verifyError);
              } else {
                console.log(`🔍 Verification query result:`, verifyPayment);
              }
            } else {
              // Verify the update actually worked
              console.log(`✅ Update response for payment ${payment.id}:`, {
                paymentType: payment.payment_type || 'final',
                status: payment.status || 'pending',
                amount: payment.amount,
                old_project_id: payment.project_id,
                new_project_id: updatedPayment.project_id,
                updatedPayment: updatedPayment
              });
              
              if (updatedPayment.project_id !== localProposal.project_id) {
                console.error(`❌ CRITICAL: Update did not set project_id correctly!`, {
                  expected: localProposal.project_id,
                  actual: updatedPayment.project_id,
                  paymentId: payment.id
                });
                
                // Try to update again with explicit error handling
                console.log(`🔄 Retrying update for payment ${payment.id}...`);
                const { error: retryError, data: retryUpdated } = await supabase
                  .from('payments')
                  .update({ project_id: localProposal.project_id })
                  .eq('id', payment.id)
                  .select('project_id')
                  .single();
                
                if (retryError) {
                  console.error(`❌ Retry also failed:`, retryError);
                } else {
                  console.log(`✅ Retry result:`, retryUpdated);
                }
              } else {
                console.log(`✅ Confirmed: Payment ${payment.id} now has project_id = ${localProposal.project_id}`);
              }
            }
          }
        } else {
          console.log('ℹ️ No payments found for this proposal');
        }
      }

      toast.success(`Successfully added ${approvedServices.length} service(s) to the project.`);

      // Dispatch custom event to notify ProjectInfoModal to refresh services
      if (localProposal.project_id) {
        window.dispatchEvent(new CustomEvent('projectServicesUpdated', {
          detail: { projectId: localProposal.project_id }
        }));
        console.log('📦 Dispatched projectServicesUpdated event for project:', localProposal.project_id);
      }

      // Refresh the proposal data
      if (onUpdate && localProposal.id) {
        const updatedProposal = await getProposal(localProposal.id);
        onUpdate(updatedProposal);
        setLocalProposal(updatedProposal);
      }

      setIsConvertOptionsModalOpen(false);
    } catch (err) {
      console.error('Error converting proposal to project:', err);
      setError(err instanceof Error ? err.message : 'Failed to convert proposal to project');
      toast.error(err instanceof Error ? err.message : 'Failed to convert proposal to project');
    } finally {
      setIsConvertingToProject(false);
    }
  };
  
  // Check Twilio and Gmail connection status
  useEffect(() => {
    const checkIntegrations = async () => {
      if (!selectedBusiness?.id) return;

      try {
        // Check Twilio connection
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('twilio_enabled, twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER')
          .eq('id', selectedBusiness.id)
          .single();

        if (!businessError && businessData) {
          const twilioConnected = !!(
            businessData.twilio_enabled &&
            businessData.twilio_account_sid &&
            businessData.twilio_auth_token &&
            businessData.VITE_TWILIO_PHONE_NUMBER
          );
          setHasTwilioConnected(twilioConnected);
        }

        // Check Gmail connection
        const { data: gmailConnections, error: gmailError } = await supabase
          .from('gmail_connections')
          .select('id, is_active')
          .eq('business_id', selectedBusiness.id)
          .eq('is_active', true)
          .limit(1);

        if (gmailError) {
          console.error('Error checking Gmail connection:', gmailError);
          setHasGmailConnected(false);
        } else {
          setHasGmailConnected((gmailConnections || []).length > 0);
        }

        // Check SendGrid marketing verified sender (fallback for email)
        const { data: marketingSettings, error: marketingError } = await supabase
          .from('email_marketing_settings')
          .select('verified_senders')
          .eq('business_id', selectedBusiness.id)
          .single();

        if (marketingError) {
          setHasSendGridMarketing(false);
        } else {
          const senders = marketingSettings?.verified_senders as Array<{ email: string; verification_status: string }> | null;
          const hasVerified = Array.isArray(senders) && senders.some(s => s.verification_status === 'verified');
          setHasSendGridMarketing(hasVerified);
        }
      } catch (error) {
        console.error('Error checking integrations:', error);
      }
    };

    if (isOpen && selectedBusiness?.id) {
      checkIntegrations();
    }
  }, [isOpen, selectedBusiness?.id]);

  // Load calendar booking type settings
  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id || !hasCalendarApp || !supabase) return;

    const loadCalendarSettings = async () => {
      try {
        const { data: locData } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'show_location_booking_in_fab',
          p_default_value: true
        });
        if (locData !== null) setShowLocationBooking(locData);

        const { data: mobData } = await supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'show_mobile_booking_in_fab',
          p_default_value: true
        });
        if (mobData !== null) setShowMobileBooking(mobData);
      } catch (err) {
        console.error('Error loading calendar booking settings:', err);
      }
    };

    loadCalendarSettings();
  }, [isOpen, selectedBusiness?.id, hasCalendarApp]);

  useEffect(() => {
    setLocalProposal(proposal);
    
    // Load proposal events when the modal is opened
    if (isOpen && proposal?.id) {
      console.log('Modal opened, loading events for proposal:', proposal.id);
      loadProposalEvents(proposal.id);
      
      // Load business name
      loadBusinessName(proposal.business_id);
      
      // Load product photos
      loadProductPhotos();
      
      // Load proposal photos
      loadProposalPhotos(proposal.id);

      // Refresh services from the server so each line carries its line_key,
      // which is required to match attached per-service photos.
      getProposal(proposal.id).then((fresh) => { if (fresh) setLocalProposal(fresh); }).catch(() => {});
      
      // Load full customer object if customer_id is available
      if (proposal.customer_id) {
        loadFullCustomer(proposal.customer_id);
      }

      // Load project if project_id is available
      if (proposal.project_id) {
        loadProject(proposal.project_id);
      } else {
        setProject(null);
      }

      // Load opportunity if pipeline_opportunities_id is available
      if (proposal.pipeline_opportunities_id) {
        loadOpportunity(proposal.pipeline_opportunities_id);
      } else {
        setOpportunity(null);
      }

      // Load booking if booking_id is available
      if ((proposal as any).booking_id) {
        loadLinkedBooking((proposal as any).booking_id);
      } else {
        setLinkedBooking(null);
      }
      
      // Reset auto-convert ref when modal opens to allow conversion check
      // This handles the case where proposal is already approved when modal opens
      autoConvertRef.current = null;
      if (proposal.project_id) {
        lastProjectIdRef.current = proposal.project_id;
      }
    }
  }, [proposal, isOpen]);

  // Set up realtime subscription to detect proposal status changes (e.g., from public page approval)
  useEffect(() => {
    if (!isOpen || !localProposal?.id || !localProposal.business_id) {
      return;
    }

    console.log('Setting up realtime subscription for proposal:', localProposal.id);

    const proposalChannel = supabase
      .channel(`proposal_details_${localProposal.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'proposals',
          filter: `id=eq.${localProposal.id}`
        },
        async (payload) => {
          console.log('Proposal updated via realtime:', payload);
          if (payload.new) {
            const updatedProposalData = payload.new as any;
            
            // If status changed, refetch the full proposal to get updated offers
            if (updatedProposalData.status !== localProposal.status) {
              console.log('Proposal status changed via realtime:', {
                oldStatus: localProposal.status,
                newStatus: updatedProposalData.status
              });
              
              // If status changed to 'approved', reset the conversion ref to allow auto-conversion
              if (updatedProposalData.status === 'approved') {
                console.log('Status changed to approved, resetting conversion ref');
                autoConvertRef.current = null;
              }
              
              try {
                const updatedProposal = await getProposal(localProposal.id);
                setLocalProposal(updatedProposal);
                
                // Notify parent component
                if (onUpdate) {
                  onUpdate(updatedProposal);
                }
              } catch (error) {
                console.error('Error fetching updated proposal:', error);
              }
            } else {
              // Just update local proposal with new data
              setLocalProposal(prev => prev ? { ...prev, ...updatedProposalData } : null);
            }
          }
        }
      )
      .subscribe();

    return () => {
      console.log('Cleaning up realtime subscription for proposal:', localProposal.id);
      supabase.removeChannel(proposalChannel);
    };
  }, [isOpen, localProposal?.id, localProposal?.business_id, localProposal?.status]);

  // Auto-convert proposal to project/booking when approved and linked
  const autoConvertRef = useRef<string | null>(null);
  const lastProjectIdRef = useRef<string | null>(null);
  const lastBookingIdRef = useRef<string | null>(null);
  
  useEffect(() => {
    if (localProposal?.id && autoConvertRef.current !== localProposal.id) {
      autoConvertRef.current = null;
      lastProjectIdRef.current = null;
      lastBookingIdRef.current = null;
    }
    
    // Reset if project_id changes for an approved proposal
    if (
      localProposal?.id &&
      localProposal.status === 'approved' &&
      localProposal.project_id &&
      lastProjectIdRef.current !== localProposal.project_id
    ) {
      autoConvertRef.current = null;
      lastProjectIdRef.current = localProposal.project_id;
    }

    // Reset if booking_id changes for an approved proposal
    if (
      localProposal?.id &&
      localProposal.status === 'approved' &&
      (localProposal as any).booking_id &&
      lastBookingIdRef.current !== (localProposal as any).booking_id
    ) {
      autoConvertRef.current = null;
      lastBookingIdRef.current = (localProposal as any).booking_id;
    }
  }, [localProposal?.id, localProposal?.project_id, (localProposal as any)?.booking_id, localProposal?.status]);

  useEffect(() => {
    const hasProjectLink = localProposal?.project_id;
    const hasBookingLink = (localProposal as any)?.booking_id;

    if (
      isOpen &&
      localProposal &&
      localProposal.status === 'approved' &&
      (hasProjectLink || hasBookingLink) &&
      localProposal.offers &&
      localProposal.offers.length > 0 &&
      !isConvertingToProject &&
      !isConvertModalOpen &&
      !isConvertOptionsModalOpen &&
      autoConvertRef.current !== localProposal.id
    ) {
      const approvedOffer = localProposal.offers.find(offer => offer.status === 'approved');
      if (!approvedOffer) return;
      
      const approvedServices = approvedOffer.services || [];
      if (approvedServices.length === 0) return;
      
      autoConvertRef.current = localProposal.id;
      
      const timeoutId = setTimeout(() => {
        if (hasBookingLink) {
          handleConvertToBooking();
        } else if (hasProjectLink) {
          handleConvertToProject();
        }
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [localProposal?.status, localProposal?.project_id, (localProposal as any)?.booking_id, localProposal?.id, localProposal?.offers, isOpen, isConvertingToProject, isConvertModalOpen, isConvertOptionsModalOpen]);

  const loadFullCustomer = async (customerId: string) => {
    try {
      const customer = await getCustomer(customerId);
      setFullCustomer(customer);
    } catch (error) {
      console.error('Error loading customer:', error);
      setFullCustomer(null);
    }
  };

  const loadProposalPhotos = async (proposalId: string) => {
    try {
      const photos = await getProposalPhotos(proposalId);
      setProposalPhotos(photos);
      console.log('📸 Loaded proposal photos in details modal:', photos.length);
    } catch (error) {
      console.error('Error loading proposal photos:', error);
      setProposalPhotos([]);
    }
    // Per-service photos (grouped by line_key).
    try {
      const svcPhotos = await listProposalServicePhotos(proposalId);
      setServicePhotosByKey(groupPhotosByLineKey(svcPhotos));
    } catch (error) {
      console.error('Error loading per-service photos:', error);
      setServicePhotosByKey({});
    }
    // Robust line_key lookup straight from the base table. Key it by every stable
    // identifier the rendered service might expose (proposal_services id,
    // template service_id, and name) so the match works no matter which shape the
    // proposal object passed into this modal happens to use.
    try {
      const { data: svcRows } = await supabase
        .from('proposal_services')
        .select('id, service_id, name, line_key')
        .eq('proposal_id', proposalId);
      const map: Record<string, string> = {};
      (svcRows || []).forEach((r: any) => {
        if (!r?.line_key) return;
        if (r.id) map[String(r.id)] = r.line_key as string;
        if (r.service_id) map[String(r.service_id)] = r.line_key as string;
        if (r.name) map[`name:${r.name}`] = r.line_key as string;
      });
      setLineKeyByServiceId(map);
    } catch (error) {
      console.error('Error loading service line keys:', error);
      setLineKeyByServiceId({});
    }
  };

  const handleDeleteServicePhoto = async (photoId: string, lineKey: string) => {
    const ok = await deleteProposalServicePhoto(photoId);
    if (ok) {
      setServicePhotosByKey(prev => ({
        ...prev,
        [lineKey]: (prev[lineKey] || []).filter(p => p.id !== photoId),
      }));
    }
  };

  const loadProject = async (projectId: string) => {
    setIsLoadingProject(true);
    try {
      const { data, error } = await supabase
        .from('projects_active')
        .select('id, title')
        .eq('id', projectId)
        .single();

      if (error) {
        console.error('Error loading project:', error);
        setProject(null);
      } else if (data) {
        setProject({ id: data.id, title: data.title });
      }
    } catch (error) {
      console.error('Error loading project:', error);
      setProject(null);
    } finally {
      setIsLoadingProject(false);
    }
  };

  const loadLinkedBooking = async (bookingId: string) => {
    setIsLoadingBooking(true);
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select('id, title, booking_number, status, mobile_booking')
        .eq('id', bookingId)
        .single();

      if (error) {
        console.error('Error loading booking:', error);
        setLinkedBooking(null);
      } else if (data) {
        setLinkedBooking(data);
      }
    } catch (error) {
      console.error('Error loading booking:', error);
      setLinkedBooking(null);
    } finally {
      setIsLoadingBooking(false);
    }
  };

  const loadOpportunity = async (opportunityId: number) => {
    setIsLoadingOpportunity(true);
    try {
      const { data, error } = await supabase
        .from('pipeline_opportunities')
        .select('id, title')
        .eq('id', opportunityId)
        .single();

      if (error) {
        console.error('Error loading opportunity:', error);
        setOpportunity(null);
      } else if (data) {
        setOpportunity({ id: data.id, title: data.title });
      }
    } catch (error) {
      console.error('Error loading opportunity:', error);
      setOpportunity(null);
    } finally {
      setIsLoadingOpportunity(false);
    }
  };

  // Fetch projects for link modal
  const fetchProjects = async () => {
    if (!localProposal.business_id || !supabase) return;

    setIsLoadingProjects(true);
    try {
      const proposalCustomerId = localProposal.customer_id;
      
      let query = supabase
        .from('projects')
        .select(`
          id,
          title,
          status,
          customer_id,
          proposal_id,
          customer:customers!projects_customer_id_fkey(
            id,
            first_name,
            last_name
          )
        `)
        .eq('business_id', localProposal.business_id)
        .eq('is_deleted', false);
      
      // Only show projects with no customer OR matching customer
      if (proposalCustomerId) {
        query = query.or(`customer_id.is.null,customer_id.eq.${proposalCustomerId}`);
      } else {
        // If proposal has no customer, only show projects with no customer
        query = query.is('customer_id', null);
      }
      
      const { data: allProjects, error } = await query
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching projects:', error);
        toast.error('Failed to load projects');
        return;
      }

      // Filter to show only projects without proposals linked, OR the currently linked project (if any)
      const filtered = (allProjects || []).filter(project => 
        project.proposal_id === null || 
        (localProposal.project_id && project.id === localProposal.project_id)
      );

      setProjects(filtered);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // Filter projects based on search query
  const filteredProjects = projects.filter(project => {
    const query = projectSearchQuery.toLowerCase();
    return (
      project.title?.toLowerCase().includes(query) ||
      project.customer?.first_name?.toLowerCase().includes(query) ||
      project.customer?.last_name?.toLowerCase().includes(query) ||
      `${project.customer?.first_name} ${project.customer?.last_name}`.toLowerCase().includes(query)
    );
  });

  // Fetch opportunities for link modal
  const fetchOpportunities = async () => {
    if (!localProposal.business_id || !supabase) return;

    setIsLoadingOpportunities(true);
    try {
      const proposalCustomerId = localProposal.customer_id;
      
      let query = supabase
        .from('pipeline_opportunities')
        .select(`
          id,
          title,
          customer_id,
          status,
          value
        `)
        .eq('business_id', localProposal.business_id);
      
      // Only show opportunities with no customer OR matching customer
      if (proposalCustomerId) {
        query = query.or(`customer_id.is.null,customer_id.eq.${proposalCustomerId}`);
      } else {
        // If proposal has no customer, only show opportunities with no customer
        query = query.is('customer_id', null);
      }
      
      const { data: opportunitiesData, error } = await query
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching opportunities:', error);
        toast.error('Failed to load opportunities');
        return;
      }

      // Fetch customer data separately for each opportunity
      const opportunitiesWithCustomers = await Promise.all(
        (opportunitiesData || []).map(async (opp) => {
          if (!opp.customer_id) {
            return { ...opp, customer: null };
          }
          
          const { data: customerData, error: customerError } = await supabase
            .from('customers')
            .select('id, first_name, last_name')
            .eq('id', opp.customer_id)
            .single();
          
          return {
            ...opp,
            customer: customerError ? null : customerData
          };
        })
      );

      setOpportunities(opportunitiesWithCustomers);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      toast.error('Failed to load opportunities');
    } finally {
      setIsLoadingOpportunities(false);
    }
  };

  // Filter opportunities based on search query
  const filteredOpportunities = opportunities.filter(opportunity => {
    const query = opportunitySearchQuery.toLowerCase();
    return (
      opportunity.title?.toLowerCase().includes(query) ||
      opportunity.customer?.first_name?.toLowerCase().includes(query) ||
      opportunity.customer?.last_name?.toLowerCase().includes(query) ||
      `${opportunity.customer?.first_name} ${opportunity.customer?.last_name}`.toLowerCase().includes(query)
    );
  });

  // Handle opportunity selection/unlinking
  const handleOpportunityLinkChange = async (opportunityId: number | null) => {
    try {
      const { error } = await supabase
        .from('proposals')
        .update({ pipeline_opportunities_id: opportunityId })
        .eq('id', localProposal.id);

      if (error) {
        console.error('Error updating opportunity link:', error);
        toast.error('Failed to update opportunity link');
        return;
      }

      // Update local proposal
      const updatedProposal = { ...localProposal, pipeline_opportunities_id: opportunityId };
      setLocalProposal(updatedProposal);

      // Load the new opportunity
      if (opportunityId) {
        await loadOpportunity(opportunityId);
      } else {
        setOpportunity(null);
      }

      // Update parent component
      if (onUpdate) {
        onUpdate(updatedProposal);
      }

      toast.success(opportunityId ? 'Opportunity linked successfully' : 'Opportunity unlinked successfully');
      setIsLinkModalOpen(false);
      setLinkModalView('options');
      setOpportunitySearchQuery('');
    } catch (error) {
      console.error('Error updating opportunity link:', error);
      toast.error('Failed to update opportunity link');
    }
  };

  // Handle project selection/unlinking
  const handleProjectLinkChange = async (projectId: string | null) => {
    try {
      const { error } = await supabase
        .from('proposals')
        .update({ project_id: projectId })
        .eq('id', localProposal.id);

      if (error) {
        console.error('Error updating proposal project:', error);
        toast.error('Failed to update project link');
        return;
      }

      // Update local proposal
      const updatedProposal = { ...localProposal, project_id: projectId };
      setLocalProposal(updatedProposal);

      // Update project in database if linking
      if (projectId) {
        // Update project with proposal_id
        const { error: projectError } = await supabase
          .from('projects')
          .update({ proposal_id: localProposal.id })
          .eq('id', projectId)
          .eq('business_id', localProposal.business_id);

        if (projectError) {
          console.error('Error updating project proposal_id:', projectError);
        }

        // Apply proposal payments to the project
        console.log('🔄 Applying proposal payments to project:', {
          proposalId: localProposal.id,
          projectId: projectId
        });
        
        // Find all payments for this proposal
        const { data: proposalPayments, error: paymentsError } = await supabase
          .from('payments')
          .select('*')
          .eq('proposal_id', localProposal.id);
        
        if (paymentsError) {
          console.error('❌ Error fetching proposal payments:', paymentsError);
        } else if (proposalPayments && proposalPayments.length > 0) {
          console.log(`📦 Found ${proposalPayments.length} payment(s) to link to project ${projectId}`);
          
          // Update each payment to link it to the project
          for (const payment of proposalPayments) {
            const updateData: any = { 
              project_id: projectId,
            };
            
            // Preserve payment_type if it exists
            if (payment.payment_type) {
              updateData.payment_type = payment.payment_type;
            }
            
            const { error: updatePaymentError } = await supabase
              .from('payments')
              .update(updateData)
              .eq('id', payment.id);
            
            if (updatePaymentError) {
              console.error(`❌ Error updating payment ${payment.id}:`, updatePaymentError);
            } else {
              console.log(`✅ Linked payment ${payment.id} to project ${projectId}`);
            }
          }
        } else {
          console.log('ℹ️ No payments found for this proposal');
        }

        // Load the new project
        await loadProject(projectId);
      } else {
        // Unlinking - clear project from old project if it exists
        if (localProposal.project_id) {
          const { error: unlinkError } = await supabase
            .from('projects')
            .update({ proposal_id: null })
            .eq('id', localProposal.project_id)
            .eq('business_id', localProposal.business_id);

          if (unlinkError) {
            console.error('Error unlinking project:', unlinkError);
          }

          // Remove project_id from proposal payments when unlinking
          const { data: proposalPayments } = await supabase
            .from('payments')
            .select('*')
            .eq('proposal_id', localProposal.id)
            .eq('project_id', localProposal.project_id);
          
          if (proposalPayments && proposalPayments.length > 0) {
            for (const payment of proposalPayments) {
              await supabase
                .from('payments')
                .update({ project_id: null })
                .eq('id', payment.id);
            }
            console.log(`✅ Removed project_id from ${proposalPayments.length} payment(s)`);
          }
        }
        setProject(null);
      }

      // Update parent component
      if (onUpdate) {
        onUpdate(updatedProposal);
      }

      toast.success(projectId ? 'Project linked successfully' : 'Project unlinked successfully');
      setIsLinkModalOpen(false);
      setLinkModalView('options');
      setProjectSearchQuery('');
    } catch (error) {
      console.error('Error updating project link:', error);
      toast.error('Failed to update project link');
    }
  };

  const fetchBookingsForLink = async () => {
    if (!localProposal.business_id || !supabase) return;

    setIsLoadingBookings(true);
    try {
      const proposalCustomerId = localProposal.customer_id;

      let query = supabase
        .from('bookings')
        .select('id, title, booking_number, status, customer_id, customer:customers!bookings_customer_id_fkey(id, first_name, last_name)')
        .eq('business_id', localProposal.business_id)
        .eq('is_deleted', false);

      if (proposalCustomerId) {
        query = query.eq('customer_id', proposalCustomerId);
      }

      const { data, error } = await query
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) {
        console.error('Error fetching bookings:', error);
        toast.error('Failed to load bookings');
        return;
      }

      setBookingsForLink(data || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setIsLoadingBookings(false);
    }
  };

  const filteredBookings = bookingsForLink.filter(b => {
    const query = bookingSearchQuery.toLowerCase();
    return (
      b.title?.toLowerCase().includes(query) ||
      String(b.booking_number).includes(query) ||
      b.customer?.first_name?.toLowerCase().includes(query) ||
      b.customer?.last_name?.toLowerCase().includes(query) ||
      `${b.customer?.first_name} ${b.customer?.last_name}`.toLowerCase().includes(query)
    );
  });

  const handleBookingLinkChange = async (bookingId: string | null) => {
    try {
      const { error } = await supabase
        .from('proposals')
        .update({ booking_id: bookingId })
        .eq('id', localProposal.id);

      if (error) {
        console.error('Error updating proposal booking:', error);
        toast.error('Failed to update booking link');
        return;
      }

      const updatedProposal = { ...localProposal, booking_id: bookingId } as any;
      setLocalProposal(updatedProposal);

      if (bookingId) {
        await loadLinkedBooking(bookingId);
      } else {
        setLinkedBooking(null);
      }

      if (onUpdate) {
        onUpdate(updatedProposal);
      }

      toast.success(bookingId ? 'Booking linked successfully' : 'Booking unlinked successfully');
      setIsLinkModalOpen(false);
      setLinkModalView('options');
      setBookingSearchQuery('');
    } catch (error) {
      console.error('Error updating booking link:', error);
      toast.error('Failed to update booking link');
    }
  };

  const loadBusinessName = async (businessId: string) => {
    try {
      const { data: business, error } = await supabase
        .from('businesses')
        .select('name')
        .eq('id', businessId)
        .single();
      
      if (!error && business?.name) {
        setBusinessName(business.name);
      }
    } catch (error) {
      console.error('Error fetching business name:', error);
    }
  };

  const loadProductPhotos = async () => {
    try {
      // Collect all product stock_item_ids from all offers
      const stockItemIds: string[] = [];
      
      proposal.offers?.forEach(offer => {
        (offer as any).products?.forEach((product: any) => {
          if (product.stock_item_id && !stockItemIds.includes(product.stock_item_id)) {
            stockItemIds.push(product.stock_item_id);
          }
        });
      });
      
      if (stockItemIds.length > 0) {
        const photoMap = await ProductPhotoService.getPhotosByStockItemIds(stockItemIds);
        const photoUrls: Record<string, string> = {};
        
        if (photoMap && typeof photoMap === 'object') {
          Object.entries(photoMap).forEach(([itemId, photo]) => {
            if (photo && photo.photo_url) {
              photoUrls[itemId] = photo.photo_url;
            }
          });
        }
        
        setProductPhotos(photoUrls);
      }
    } catch (error) {
      console.error('Error loading product photos:', error);
      setProductPhotos({});
    }
  };

  // Component for product icon - shows photo if available, otherwise green Package2 icon
  const ProductIcon = ({ product }: { product: any }) => {
    const photoUrl = productPhotos[product.stock_item_id];
    
    if (photoUrl) {
      return (
        <div className="h-12 w-12 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
          <img 
            src={photoUrl} 
            alt={product.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show fallback icon
              e.currentTarget.style.display = 'none';
              const parent = e.currentTarget.parentElement;
              if (parent) {
                parent.innerHTML = '<div class="h-6 w-6 text-green-600"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.29,7 12,12 20.71,7"/><line x1="12" x2="12" y1="22" y2="12"/></svg></div>';
                parent.className = "h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0";
              }
            }}
          />
        </div>
      );
    }
    
    return (
      <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
        <Package2 className="h-6 w-6 text-green-600" />
      </div>
    );
  };
  
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Close send options dropdown when clicking outside
  useEffect(() => {
    if (!showSendOptions) return;

    let mounted = true;
    const closeDropdown = (e: MouseEvent) => {
      if (!mounted) return;
      setShowSendOptions(false);
    };

    // Delay attaching so the current tap doesn't immediately close it on iOS
    const raf = requestAnimationFrame(() => {
      document.addEventListener('mousedown', closeDropdown);
    });

    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', closeDropdown);
    };
  }, [showSendOptions]);

  // Load invoice settings when switching to invoice view
  useEffect(() => {
    const fetchInvoiceSettings = async () => {
      if (activeView !== 'invoice' || !selectedBusiness?.id) return;
      
      setIsLoadingInvoiceSettings(true);
      try {
        const settings = await InvoiceSettingsService.getInvoiceSettings(selectedBusiness.id);
        if (settings) {
          setInvoiceSettings(settings);
        } else {
          // Use default settings with business info
          const defaultSettings: InvoiceSettings = {
            business_id: selectedBusiness.id,
            company_name: selectedBusiness.name || 'Your Business Name',
            company_address: selectedBusiness.address || '',
            company_phone: selectedBusiness.phone || '',
            company_email: selectedBusiness.email || '',
            company_website: '',
            ...defaultInvoiceSettings
          } as InvoiceSettings;
          setInvoiceSettings(defaultSettings);
        }
      } catch (error) {
        console.error('Error fetching invoice settings:', error);
        // Fall back to defaults
        const fallbackSettings: InvoiceSettings = {
          business_id: selectedBusiness.id,
          company_name: selectedBusiness.name || 'Your Business Name',
          company_address: selectedBusiness.address || '',
          company_phone: selectedBusiness.phone || '',
          company_email: selectedBusiness.email || '',
          company_website: '',
          ...defaultInvoiceSettings
        } as InvoiceSettings;
        setInvoiceSettings(fallbackSettings);
      } finally {
        setIsLoadingInvoiceSettings(false);
      }
    };

    fetchInvoiceSettings();
  }, [activeView, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleApproveOffer = async (offer: ProposalOffer) => {
    if (!localProposal.id) return;
    
      setLoading(true);
    try {
      await approveProposalOffer(offer.id);
      
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        
        // Reset auto-convert ref BEFORE updating state to allow auto-conversion
        // This ensures auto-conversion triggers when the proposal is approved and linked to a project
        if (updatedProposal.status === 'approved' && updatedProposal.project_id) {
          console.log('Proposal approved, resetting auto-convert ref to allow conversion');
          autoConvertRef.current = null;
        }
        
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }
    } catch (error) {
      console.error('Error approving offer:', error);
      setError('Failed to approve offer');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineOffer = async (offer: ProposalOffer) => {
    if (!localProposal.id) return;
    
      setLoading(true);
    try {
      await declineProposalOffer(offer.id);
      
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }
    } catch (error) {
      console.error('Error declining offer:', error);
      setError('Failed to decline offer');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!localProposal.id) return;
      
    const confirmed = window.confirm('Are you sure you want to delete this proposal? This action cannot be undone.');
    if (!confirmed) return;
      
    setLoading(true);
    try {
      await deleteProposal(localProposal.id);
      onClose();
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      console.error('Error deleting proposal:', error);
      setError('Failed to delete proposal');
    } finally {
      setLoading(false);
    }
  };

  const handleSendText = async () => {
    if (!localProposal.id) return;
    
    const customerPhone = localProposal.customer?.phone || '';
    
    if (!customerPhone) {
      setError('Customer phone number is missing');
      return;
    }
    
    setShowSendOptions(false);
    setIsSMSModalOpen(true);
  };

  const handleSMSSend = async (message: string) => {
    if (!localProposal.id) return;
    
    setIsSending(true);
    
    try {
      const customerPhone = localProposal.customer?.phone || '';
      const businessId = localProposal.business_id;
      
      // Send SMS using our messaging service with custom message
      const smsResult = await sendSMS({
        businessId,
        to: customerPhone,
        message: message
      });

      if (!smsResult.success) {
        throw new Error(smsResult.error || 'Failed to send SMS');
      }

      console.log('SMS sent successfully');
      setJustSent(true);

      // Update proposal status to 'sent'
      const { error: updateError } = await supabase
        .from('proposals')
        .update({ status: 'sent' })
        .eq('id', localProposal.id);

      if (updateError) {
        console.error('Error updating proposal status:', updateError);
      }

      // Log SMS sent event
      await logProposalEvent(
        localProposal.id, 
        businessId, 
        'sent_sms', 
        'Proposal sent via SMS',
        {
          to_phone: customerPhone,
          custom_message: message
        }
      );
      
      // Reload events after sending
      await loadProposalEvents(localProposal.id);
      
      // Reset justSent after 3 seconds
      setTimeout(() => setJustSent(false), 3000);
      
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }
    } catch (error) {
      console.error('Error sending proposal:', error);
      setError('Failed to send proposal via SMS');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendEmail = async () => {
    if (!localProposal.id) return;
    
    const customerEmail = localProposal.customer?.email || '';
    
    if (!customerEmail) {
      setError('Customer email is missing');
      return;
    }
    
    setShowSendOptions(false);
    setIsEmailModalOpen(true);
  };

  const handleEmailSend = async (subject: string, message: string) => {
    if (!localProposal.id) return;
    
    setIsSending(true);
    
    try {
      const customerEmail = localProposal.customer?.email || '';
      const businessId = localProposal.business_id;
      const customerFirstName = localProposal.customer?.first_name || '';
      const businessName = selectedBusiness?.name || 'your service provider';
      
      // Get or create a token for this proposal to generate the URL
      const tokenData = await getOrCreateProposalToken(localProposal.id, businessId);
      const proposalUrl = await buildProposalCustomerLink(tokenData, businessId);
      
      // Fetch business logo from booking_settings
      let logoUrl: string | undefined = undefined;
      try {
        console.log('🚀 Fetching logo for email (handleEmailSend)...');
        const { data: businessData, error: businessError } = await supabase
          .from('businesses')
          .select('booking_settings')
          .eq('id', businessId)
          .single();
        
        if (!businessError && businessData?.booking_settings) {
          logoUrl = businessData.booking_settings?.logo_url;
          console.log('✅ Logo URL fetched (handleEmailSend):', logoUrl);
        } else {
          console.warn('⚠️ Could not fetch logo (handleEmailSend):', businessError);
        }
      } catch (error) {
        console.error('❌ Error fetching logo (handleEmailSend):', error);
      }
      
      // Extract custom message
      // The default message is: "Hi {firstName},\n\nThank you for asking us to quote on your project.\n\nIf you have any questions or concerns regarding this quote, please don't hesitate to get in touch with us."
      // If the user hasn't modified it, we'll set customMessage to undefined (so the template uses its default)
      // If they have modified it, we'll use their custom text (which completely replaces the default)
      let customMessage = message.trim();
      
      // Remove any URLs from the message (the URL will be in the button, not the message)
      customMessage = customMessage.replace(proposalUrl, '').trim();
      customMessage = customMessage.replace(/https?:\/\/[^\s]+/g, '').trim();
      
      // Check if this is the default message (with greeting)
      const escapedFirstName = customerFirstName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const defaultMessagePattern = new RegExp(
        `^Hi ${escapedFirstName},?\\s*\\n\\nThank you for asking us to quote on your project\\.?\\s*\\n\\nIf you have any questions or concerns regarding this quote, please don't hesitate to get in touch with us\\.?\\s*$`,
        'i'
      );
      
      // If it matches the default message exactly, don't include custom message
      if (defaultMessagePattern.test(customMessage)) {
        customMessage = undefined;
      } else if (!customMessage || customMessage.length === 0) {
        // If message is empty after removing URLs, don't include custom message
        customMessage = undefined;
      }
      // Otherwise, use the user's custom message
      
      // Generate HTML email template with logo and custom message
      // Parameters: customerFirstName, businessName, proposalTitle, proposalUrl, logoUrl, customMessage
      const htmlEmailBody = generateProposalEmailHTML(
        customerFirstName,
        businessName,
        localProposal.title,
        proposalUrl,
        logoUrl,  // 5th parameter: logoUrl
        customMessage  // 6th parameter: customMessage
      );
      
      // Generate PDF attachment
      console.log('📄 [PDF-MODAL] Starting PDF generation...');
      let pdfAttachment: { filename: string; content: string; type: string } | null = null;
      
      try {
        // Fetch full proposal data with offers, services, and products
        console.log('🔍 [PDF-MODAL] Fetching full proposal data...');
        const fullProposal = await getProposal(localProposal.id);
        console.log('✅ [PDF-MODAL] Full proposal fetched:', {
          proposalId: fullProposal.id,
          hasOffers: !!fullProposal.offers,
          offerCount: fullProposal.offers?.length || 0
        });
        
        // Fetch business data with GST/PST numbers
        console.log('🔍 [PDF-MODAL] Fetching business data...');
        const { data: businessData, error: businessDataError } = await supabase
          .from('businesses')
          .select('id, name, gst_number, pst_number')
          .eq('id', businessId)
          .single();
        
        if (businessDataError) {
          console.error('❌ [PDF-MODAL] Error fetching business data:', businessDataError);
        } else {
          console.log('✅ [PDF-MODAL] Business data fetched');
        }
        
        // Fetch invoice settings
        console.log('🔍 [PDF-MODAL] Fetching invoice settings...');
        let invoiceSettings = null;
        try {
          invoiceSettings = await InvoiceSettingsService.getInvoiceSettings(businessId);
          console.log('✅ [PDF-MODAL] Invoice settings fetched');
        } catch (error) {
          console.error('❌ [PDF-MODAL] Error fetching invoice settings:', error);
        }
        
        // Generate PDF
        console.log('📄 [PDF-MODAL] Calling generateProposalPDF...');
        const pdfData = await generateProposalPDF(fullProposal, businessData, invoiceSettings);
        
        if (pdfData) {
          // Convert blob to base64
          const reader = new FileReader();
          const base64Promise = new Promise<string>((resolve, reject) => {
            reader.onloadend = () => {
              const base64String = reader.result as string;
              // Remove data:application/pdf;base64, prefix
              const base64Content = base64String.split(',')[1];
              resolve(base64Content);
            };
            reader.onerror = reject;
          });
          reader.readAsDataURL(pdfData.blob);
          const base64Content = await base64Promise;
          
          pdfAttachment = {
            filename: pdfData.filename,
            content: base64Content,
            type: 'application/pdf'
          };
          console.log('✅ [PDF-MODAL] PDF generated successfully:', pdfData.filename);
        } else {
          console.warn('⚠️ [PDF-MODAL] PDF generation returned null');
        }
      } catch (error) {
        console.error('❌ [PDF-MODAL] Error generating PDF:', error);
        // Continue without PDF attachment
      }

      // Fetch proposal photos and prepare attachments
      const attachments: Array<{ filename: string; content: string; type: string }> = [];
      
      // Add PDF as first attachment if generated
      if (pdfAttachment) {
        console.log('📎 [PDF-MODAL] Adding PDF to attachments:', {
          filename: pdfAttachment.filename,
          type: pdfAttachment.type,
          contentLength: pdfAttachment.content.length
        });
        attachments.push(pdfAttachment);
        console.log('✅ [PDF-MODAL] PDF added to attachments. Total attachments:', attachments.length);
      } else {
        console.warn('⚠️ [PDF-MODAL] No PDF attachment to add');
      }
      
      try {
        const photos = await getProposalPhotos(localProposal.id);
        console.log('📸 Found proposal photos (handleEmailSend):', photos.length);
        
        for (const photo of photos) {
          try {
            // Download photo from storage
            const { data: fileData, error: downloadError } = await supabase.storage
              .from('user-uploads')
              .download(photo.file_path);
            
            if (downloadError || !fileData) {
              console.error('❌ Error downloading photo (handleEmailSend):', downloadError);
              continue;
            }
            
            // Convert to base64 using FileReader (more reliable for binary data)
            const reader = new FileReader();
            const base64Promise = new Promise<string>((resolve, reject) => {
              reader.onloadend = () => {
                const base64String = reader.result as string;
                // Remove data:image/jpeg;base64, prefix if present
                const base64Content = base64String.includes(',') 
                  ? base64String.split(',')[1] 
                  : base64String;
                resolve(base64Content);
              };
              reader.onerror = reject;
            });
            reader.readAsDataURL(fileData);
            const base64 = await base64Promise;
            
            attachments.push({
              filename: photo.file_name,
              content: base64,
              type: photo.mime_type || 'image/jpeg'
            });
            
            console.log('✅ Photo prepared for attachment (handleEmailSend):', {
              filename: photo.file_name,
              type: photo.mime_type || 'image/jpeg',
              contentLength: base64.length
            });
          } catch (error) {
            console.error('❌ Error processing photo (handleEmailSend):', error);
          }
        }
      } catch (error) {
        console.error('❌ Error fetching proposal photos (handleEmailSend):', error);
      }
      
      const emailResult = await sendEmailWithFallback({
        businessId,
        to: customerEmail,
        subject: subject,
        body: htmlEmailBody,
        attachments: attachments.length > 0 ? attachments : undefined
      });

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Failed to send email');
      }

      console.log('Email sent successfully');
      setJustSent(true);

      // Update proposal status to 'sent'
      const { error: updateError } = await supabase
        .from('proposals')
        .update({ status: 'sent' })
        .eq('id', localProposal.id);

      if (updateError) {
        console.error('Error updating proposal status:', updateError);
      }

      // Log email sent event
      await logProposalEvent(
        localProposal.id, 
        businessId, 
        'sent_email', 
        'Proposal sent via email',
        {
          to_email: customerEmail,
          subject: subject,
          custom_message: customMessage || undefined,
          url: proposalUrl
        }
      );
      
      // Reload events after sending
      await loadProposalEvents(localProposal.id);
      
      // Reset justSent after 3 seconds
      setTimeout(() => setJustSent(false), 3000);
      
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }
    } catch (error) {
      console.error('Error sending proposal:', error);
      setError('Failed to send proposal via email');
    } finally {
      setIsSending(false);
    }
  };

  const handleSendBoth = async () => {
    if (!localProposal.id) return;
    
    setIsSending(true);
    setShowSendOptions(false);
    
    try {
      const customerEmail = localProposal.customer?.email || '';
      const customerPhone = localProposal.customer?.phone || '';
      const businessId = localProposal.business_id;
      
      if (!customerEmail && !customerPhone) {
        setError('Customer email and phone are both missing');
        return;
      }
      
      if (!customerEmail) {
        setError('Customer email is missing');
        return;
      }
      
      if (!customerPhone) {
        setError('Customer phone number is missing');
        return;
      }
      
      await sendProposalLinkViaBoth(localProposal.id, businessId, customerEmail, customerPhone);
      setJustSent(true);
      
      // Reload events after sending
      await loadProposalEvents(localProposal.id);
      
      // Reset justSent after 3 seconds
      setTimeout(() => setJustSent(false), 3000);
      
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }
    } catch (error) {
      console.error('Error sending proposal:', error);
      setError('Failed to send proposal via both methods');
    } finally {
      setIsSending(false);
    }
  };

  const handleCopyUrl = async () => {
    try {
      setIsSending(true);
      setError(null);
      setShowSendOptions(false);

      const businessId = localProposal.business_id;
      if (!businessId) {
        setError('Business ID not found');
        return;
      }

      // Get or create a token for this proposal
      const tokenData = await getOrCreateProposalToken(localProposal.id, businessId);
      const publicUrl = await buildProposalCustomerLink(tokenData, businessId);
      
      // Copy to clipboard
      await navigator.clipboard.writeText(publicUrl);
      
      // Log the proposal URL copied event
      await logProposalEvent(
        localProposal.id, 
        businessId, 
        'url_copied', 
        'Proposal URL copied to clipboard',
        {
          url: publicUrl,
          copied_at: new Date().toISOString()
        }
      );
      
      // Update proposal status to 'sent' (same as other send methods)
      const { error: updateError } = await supabase
        .from('proposals')
        .update({ status: 'sent' })
        .eq('id', localProposal.id);

      if (updateError) {
        console.error('Error updating proposal status:', updateError);
        setError('Failed to update proposal status');
        return;
      }
      
      setUrlCopied(true);
      setJustSent(true);
      
      // Reload events after copying URL
      await loadProposalEvents(localProposal.id);
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setUrlCopied(false);
      }, 2000);

      // Reset justSent after 3 seconds (same as other send methods)
      setTimeout(() => setJustSent(false), 3000);
      
      // Update the proposal status and notify parent component
      if (onUpdate) {
        const updatedProposal = await getProposal(localProposal.id);
        setLocalProposal(updatedProposal);
        onUpdate(updatedProposal);
      }

    } catch (error) {
      console.error('Error copying proposal URL:', error);
      setError('Failed to copy proposal URL');
    } finally {
      setIsSending(false);
    }
  };

  const loadProposalEvents = async (proposalId: string) => {
      setLoadingEvents(true);
    console.log('Loading events for proposal:', proposalId);
      
    try {
      const events = await getProposalEvents(proposalId);
      console.log('Loaded events:', events);
        setProposalEvents(events);
    } catch (error) {
      console.error('Error loading proposal events:', error);
      setError('Failed to load proposal events');
    } finally {
      setLoadingEvents(false);
    }
  };

  const createTestEvent = async () => {
    if (!localProposal.id) return;
    
    try {
      await createTestProposalEventSimple(localProposal.id);
      console.log('Test event created successfully');
      
      // Reload events after creating test event
      await loadProposalEvents(localProposal.id);
    } catch (error) {
      console.error('Error creating test event:', error);
      setError('Failed to create test event');
    }
  };

  const getEventTypeInfo = (eventType: string) => {
    switch (eventType) {
      case 'sent_sms':
        return { 
          icon: <MessageSquare className="h-4 w-4 text-blue-600" />, 
          label: 'Text Message Sent', 
          color: 'text-blue-800' 
        };
      case 'sent_email':
        return { 
          icon: <Mail className="h-4 w-4 text-blue-600" />, 
          label: 'Email Sent', 
          color: 'text-blue-800' 
        };
      case 'viewed':
        return { 
          icon: <Eye className="h-4 w-4 text-purple-600" />, 
          label: 'Viewed by Customer', 
          color: 'text-purple-800' 
        };
      case 'offer_approved':
        return { 
          icon: <CheckCircle className="h-4 w-4 text-green-600" />, 
          label: 'Offer Approved', 
          color: 'text-green-800' 
        };
      case 'offer_declined':
        return { 
          icon: <X className="h-4 w-4 text-red-600" />, 
          label: 'Offer Declined', 
          color: 'text-red-800' 
        };
      case 'paid':
        return { 
          icon: <CreditCard className="h-4 w-4 text-green-600" />, 
          label: 'Payment Received', 
          color: 'text-green-800' 
        };
      case 'booked':
        const bookedLabel = localProposal.project_id && !(localProposal as any).booking_id
          ? 'Converted to Project'
          : linkedBooking?.mobile_booking
            ? 'Converted to Mobile Booking'
            : 'Converted to Location Booking';
        return { 
          icon: <CheckCircle className="h-4 w-4 text-green-600" />, 
          label: bookedLabel, 
          color: 'text-green-800' 
        };
      case 'test_event':
        return { 
          icon: <Activity className="h-4 w-4 text-gray-600" />, 
          label: 'Test Event', 
          color: 'text-gray-800' 
        };
      case 'url_copied':
        return { 
          icon: <Copy className="h-4 w-4 text-blue-600" />, 
          label: 'URL Copied', 
          color: 'text-blue-800' 
        };
      default:
        console.log('Unknown event type:', eventType);
        return { 
          icon: <Activity className="h-4 w-4 text-gray-600" />, 
          label: eventType, 
          color: 'text-gray-800' 
        };
    }
  };

  // Fetch proposal payments to calculate amount paid
  useEffect(() => {
    if (!isOpen || !localProposal?.id) return;
    
    const fetchProposalPayments = async () => {
      try {
        // Fetch all payments (including pending) to show deposit status
        const { data: payments, error } = await supabase
          .from('payments')
          .select('*')
          .eq('proposal_id', localProposal.id)
          .order('created_at', { ascending: false });
        
        if (error) {
          console.error('Error fetching proposal payments:', error);
          return;
        }
        
        // Filter out cancelled payments and keep only the most recent pending deposit
        const allPayments = payments || [];
        const filtered: typeof allPayments = [];
        let seenPendingDeposit = false;

        for (const p of allPayments) {
          if (p.status === 'cancelled') continue;
          if (p.payment_type === 'deposit' && p.status === 'pending') {
            if (seenPendingDeposit) continue;
            seenPendingDeposit = true;
          }
          filtered.push(p);
        }

        setProposalPayments(filtered);
        
        // Calculate total from completed payments only (exclude surcharges from balance calc)
        const completedPayments = filtered.filter(p => p.status === 'completed');
        const totalPaid = completedPayments.reduce((sum, payment) => sum + (payment.base_amount || payment.amount || 0), 0);
        setProposalAmountPaid(totalPaid);
      } catch (error) {
        console.error('Error calculating proposal payments:', error);
      }
    };
    
    fetchProposalPayments();
  }, [isOpen, localProposal?.id]);

  // Listen for global event to close modal
  useEffect(() => {
    const handleCloseAllModals = () => {
      if (isOpen) {
        onClose();
      }
    };

    window.addEventListener('closeAllModals', handleCloseAllModals);
    
    return () => {
      window.removeEventListener('closeAllModals', handleCloseAllModals);
    };
  }, [isOpen, onClose]);

  if (!isOpen && !isAnimating) return null;
  if (!localProposal) return null;

  const isMobile = window.innerWidth < 640;

  const modalContent = (
    <>
      <div 
        className={`fixed inset-0 transition-opacity duration-200 ${
          isOpen && !isConvertMobileModalOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ zIndex: resolvedZIndex }}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onTransitionEnd={handleTransitionEnd}
      >
        {/* Visual backdrop - pointer-events:none so it can't intercept taps */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        />
        <div 
          className={`bg-white transform transition-all duration-200 ${
            isMobile 
              ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
              : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                  isOpen ? 'translate-x-0' : 'translate-x-full'
                }`
          }`}
          style={{ 
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            zIndex: resolvedZIndex + 1,
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div className="h-full flex flex-col overflow-hidden">
            {/* Link Chain Button */}
            <button
              type="button"
              onClick={async () => {
                setIsLinkModalOpen(true);
                setLinkModalView('options');
              }}
              className={`absolute right-14 h-8 w-8 flex items-center justify-center rounded-full active:scale-95 transition-all duration-150 touch-manipulation z-10 ${
                (localProposal.project_id || localProposal.pipeline_opportunities_id || (localProposal as any).booking_id)
                  ? 'text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700' 
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200'
              }`}
              style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))', WebkitTapHighlightColor: 'transparent' }}
            >
              <Link className="h-5 w-5" />
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
              style={{ top: 'calc(1rem + env(safe-area-inset-top, 0px))', WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
              <h2 className="text-2xl font-bold text-gray-900 truncate px-8" style={{ 
                letterSpacing: '-0.02em'
              }}>
                {localProposal.title}
              </h2>
              <div className="flex items-center justify-center mt-2">
                <div className={`h-2 w-2 rounded-full ${getStatusConfig(localProposal.status).color} mr-2 flex-shrink-0`} />
                <span className="text-sm text-gray-600">{getStatusConfig(localProposal.status).description}</span>
              </div>
                
              {/* Action Buttons */}
              <div className="flex items-center justify-center gap-2 mt-4">
                {/* Send Button */}
                {!readOnly && (localProposal.status === 'draft' || localProposal.status === 'sent' || localProposal.status === 'viewed') && (
                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSendOptions(!showSendOptions);
                      }}
                      disabled={isSending || justSent}
                      className={`inline-flex items-center justify-center ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'} text-sm font-semibold rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm ${
                        justSent 
                          ? 'text-green-700 bg-green-50 hover:bg-green-100 active:bg-green-200 border border-green-200' 
                          : 'text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                      } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                      title={isSending ? 'Sending...' : justSent ? 'Sent!' : localProposal.status === 'draft' ? 'Send Proposal' : 'Send Again'}
                    >
                      {isSending ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      ) : justSent ? (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      {isSending ? 'Sending...' : justSent ? 'Sent!' : 'Send'}
                    </button>
                    
                    {showSendOptions && (
                      <div 
                        className="absolute left-0 top-full mt-2 w-52 rounded-xl shadow-lg bg-white border border-gray-300 z-20"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="py-1" role="menu" aria-orientation="vertical">
                          {hasTwilioConnected && (
                            <button
                              onClick={handleSendText}
                              className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 active:scale-[0.98] touch-manipulation rounded-lg mx-1"
                              style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                              role="menuitem"
                            >
                              <MessageSquare className="h-4 w-4 mr-3 text-gray-500" />
                              SMS
                            </button>
                          )}
                          {/* Email always works — with no Gmail/verified sender it
                              sends from the platform default (noreply + business name). */}
                          <button
                            onClick={handleSendEmail}
                            className="w-full px-4 py-3 text-sm text-left text-gray-700 hover:bg-gray-50 active:bg-gray-100 flex items-center transition-all duration-150 active:scale-[0.98] touch-manipulation rounded-lg mx-1"
                            style={{ minHeight: '44px', WebkitTapHighlightColor: 'transparent' }}
                            role="menuitem"
                          >
                            <Mail className="h-4 w-4 mr-3 text-gray-500" />
                            Email
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Edit Button */}
                {!readOnly && localProposal.status !== 'booked' && (
                  <button
                    onClick={() => setIsEditProposalModalOpen(true)}
                    disabled={loading}
                    className={`inline-flex items-center justify-center ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'} text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title="Edit Proposal"
                  >
                    <Pencil className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                )}
                
                {/* Delete Button */}
                {!readOnly && localProposal.status !== 'booked' && (
                  <button
                    onClick={handleDelete}
                    disabled={loading}
                    className={`inline-flex items-center justify-center ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'} text-sm font-semibold text-white bg-red-600 hover:bg-red-700 active:bg-red-800 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                    title="Delete Proposal"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                )}
              </div>

              {/* Convert Button */}
              {localProposal && ((!readOnly && localProposal.status === 'approved') || localProposal.status === 'booked') && (
                <div className="mt-4 space-y-2">
                  {!readOnly && localProposal.status === 'approved' && (
                    <button
                      onClick={() => {
                        setIsConvertOptionsModalOpen(true);
                      }}
                      className={`w-full inline-flex items-center justify-center ${isMobile ? 'px-4 py-3' : 'px-6 py-3.5'} text-base font-semibold text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Calendar className="h-5 w-5 mr-2" />
                      Convert
                    </button>
                  )}
                  {localProposal.status === 'booked' && (
                    <div className={`w-full flex items-center justify-center ${isMobile ? 'px-4 py-3' : 'px-6 py-3.5'} text-base font-semibold text-green-700 bg-green-50 rounded-xl border border-green-200`}>
                      {localProposal.project_id && !(localProposal as any).booking_id ? (
                        <>
                          <Folder className="h-5 w-5 mr-2" />
                          Converted to Project
                        </>
                      ) : linkedBooking?.mobile_booking ? (
                        <>
                          <Truck className="h-5 w-5 mr-2" />
                          Converted to Mobile Booking
                        </>
                      ) : (
                        <>
                          <MapPin className="h-5 w-5 mr-2" />
                          Converted to Location Booking
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* View Toggle Tabs */}
            <div className="bg-white border-b border-gray-200 px-4">
              <div className="flex space-x-1">
                <button
                  onClick={() => setActiveView('details')}
                  className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-all duration-150 ${
                    activeView === 'details'
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Details
                </button>
                <button
                  onClick={() => setActiveView('invoice')}
                  className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-all duration-150 ${
                    activeView === 'invoice'
                      ? 'text-blue-600 border-blue-600'
                      : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Receipt className="h-4 w-4 mr-2" />
                  Invoice
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-white" style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}>
              {/* Details View */}
              {activeView === 'details' && (
              <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6`} style={{
                paddingBottom: isMobile ? 'calc(1rem + env(safe-area-inset-bottom, 0px))' : '1rem'
              }}>
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                      {error}
                    </div>
                  </div>
                )}

                <div className="space-y-6">
                  {/* Project Info - Conditionally Rendered */}
                  {localProposal.project_id && (
                    <>
                      {isLoadingProject ? (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="p-2">
                            <div className="text-sm text-gray-500">Loading project...</div>
                          </div>
                        </div>
                      ) : project ? (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="p-2">
                            <div className="flex items-center space-x-2.5">
                              <button
                                type="button"
                                onClick={async () => {
                                  setIsLinkModalOpen(true);
                                  if (localProposal.pipeline_opportunities_id) {
                                    setLinkModalView('opportunities');
                                    await fetchOpportunities();
                                  } else {
                                    setLinkModalView('projects');
                                    await fetchProjects();
                                  }
                                }}
                                className="h-6 w-6 flex items-center justify-center text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-full active:scale-95 transition-all duration-150 flex-shrink-0"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <Link className="h-4 w-4" />
                              </button>
                              <div className="text-[14px] font-semibold text-gray-900 truncate leading-tight flex-1 min-w-0">
                                {project.title}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : opportunity ? (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="px-3 py-2 border-b border-gray-200/60">
                            <div className="text-[13px] font-semibold text-gray-900">Linked Opportunity</div>
                          </div>
                          <div className="p-2">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={async () => {
                                  setIsLinkModalOpen(true);
                                  setLinkModalView('opportunities');
                                  await fetchOpportunities();
                                }}
                                className="h-6 w-6 flex items-center justify-center text-green-600 bg-green-50 hover:bg-green-100 rounded-full active:scale-95 transition-all duration-150 flex-shrink-0"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <Link className="h-4 w-4" />
                              </button>
                              <div className="text-[14px] font-semibold text-gray-900 truncate leading-tight flex-1 min-w-0">
                                {opportunity.title}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="p-2">
                            <div className="text-sm text-gray-500">Unable to load project</div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Booking Info - Conditionally Rendered */}
                  {(localProposal as any).booking_id && (
                    <>
                      {isLoadingBooking ? (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="p-2">
                            <div className="text-sm text-gray-500">Loading booking...</div>
                          </div>
                        </div>
                      ) : linkedBooking ? (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="p-2">
                            <div className="flex items-center space-x-2.5">
                              <button
                                type="button"
                                onClick={async () => {
                                  setIsLinkModalOpen(true);
                                  setLinkModalView('bookings');
                                  await fetchBookingsForLink();
                                }}
                                className="h-6 w-6 flex items-center justify-center text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-full active:scale-95 transition-all duration-150 flex-shrink-0"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                <Link className="h-4 w-4" />
                              </button>
                              <div className="text-[14px] font-semibold text-gray-900 truncate leading-tight flex-1 min-w-0">
                                {linkedBooking.title || `Booking #${linkedBooking.booking_number}`}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                          <div className="p-2">
                            <div className="text-sm text-gray-500">Unable to load booking</div>
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {/* Customer Info - iOS Style - Matching BookingCustomerInfo */}
                  <div className="relative">
                    <div 
                      className="bg-white rounded-[12px] border border-gray-200/60 p-2 relative transition-all duration-150 shadow-[0_1px_2px_rgba(0,0,0,0.05)] cursor-pointer active:scale-[0.98] touch-manipulation"
                      onClick={(e) => {
                        // Stop propagation if clicking on action buttons area
                        const target = e.target as HTMLElement;
                        if (target.closest('.customer-action-buttons') || target.closest('button[class*="border"]')) {
                          e.stopPropagation();
                          return;
                        }
                        if (localProposal.customer) {
                          setIsCustomerDetailsModalOpen(true);
                        }
                      }}
                      onMouseDown={(e) => {
                        // Stop propagation if clicking on action buttons area
                        const target = e.target as HTMLElement;
                        if (target.closest('.customer-action-buttons') || target.closest('button[class*="border"]')) {
                          e.stopPropagation();
                        }
                      }}
                      style={{
                        WebkitTapHighlightColor: 'transparent',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                      }}
                    >
                      {/* Top row: Customer name and avatar */}
                      <div className="flex items-center space-x-2.5 mb-2">
                        {/* Avatar - Top Left, Smaller, Inline with name */}
                        <div className="h-6 w-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0">
                          {localProposal.customer?.first_name?.[0]}{localProposal.customer?.last_name?.[0]}
                        </div>
                        <div className="text-[14px] font-semibold text-gray-900 truncate leading-tight flex-1 min-w-0">
                          {localProposal.customer?.first_name} {localProposal.customer?.last_name}
                        </div>
                      </div>
                      
                      {/* Dividing line */}
                      <div className="border-t border-gray-200/60 my-2"></div>
                      
                      {/* Action Icons - Below dividing line */}
                      <div className="flex items-center space-x-1.5 relative z-10 customer-action-buttons pt-1" onClick={(e) => e.stopPropagation()}>
                        {canAccess.messages && localProposal.customer?.phone && (
                          <>
                            <button
                              className="h-8 w-8 flex items-center justify-center border border-gray-200/60 bg-transparent text-green-600 rounded-[8px] hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 active:scale-95 transition-all duration-150 touch-manipulation relative z-10"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (localProposal.customer?.phone) {
                                  window.dispatchEvent(new CustomEvent('closeAllModals'));
                                  setTimeout(() => {
                                    if (localProposal.customer?.phone) {
                                      window.dispatchEvent(new CustomEvent('openDialler', {
                                        detail: { phoneNumber: localProposal.customer.phone }
                                      }));
                                    }
                                  }, 100);
                                }
                              }}
                              onMouseDown={(e) => {
                                e.stopPropagation();
                              }}
                              onTouchStart={(e) => {
                                e.stopPropagation();
                              }}
                              style={{ 
                                WebkitTapHighlightColor: 'transparent',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                                pointerEvents: 'auto',
                                zIndex: 10
                              }}
                              title="Call customer"
                            >
                              <Phone className="h-4 w-4" />
                            </button>
                            <button
                              className="h-8 w-8 flex items-center justify-center border border-gray-200/60 bg-transparent text-green-600 rounded-[8px] hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 active:scale-95 transition-all duration-150 touch-manipulation"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMessageModalComposerType('sms');
                                setIsMessageModalOpen(true);
                              }}
                              style={{ 
                                WebkitTapHighlightColor: 'transparent',
                                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                              }}
                              title="Message customer"
                            >
                              <MessageSquare className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        {canAccess.messages && localProposal.customer?.email && (
                          <button
                            className="h-8 w-8 flex items-center justify-center border border-gray-200/60 bg-transparent text-red-600 rounded-[8px] hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 active:scale-95 transition-all duration-150 touch-manipulation relative z-10"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (localProposal.customer?.email) {
                                setMessageModalComposerType('email');
                                setIsMessageModalOpen(true);
                              }
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                            }}
                            style={{ 
                              WebkitTapHighlightColor: 'transparent',
                              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                              pointerEvents: 'auto',
                              zIndex: 10
                            }}
                            title="Email customer"
                          >
                            <Mail className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Decline Reason - Only show if proposal is declined and has decline reason */}
                  {localProposal.status === 'declined' && localProposal.notes && (() => {
                    try {
                      const notesData = JSON.parse(localProposal.notes);
                      const declineReason = notesData.decline_reason;
                      
                      if (declineReason) {
                        return (
                          <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
                            <div className="bg-red-50 border-b border-red-200 px-4 py-3">
                              <h3 className="text-base font-semibold text-gray-900 flex items-center">
                                <AlertCircle className="h-4 w-4 mr-2 text-red-600" />
                                Decline Reason
                              </h3>
                            </div>
                            <div className="p-4">
                              <p className="text-sm text-gray-800 bg-red-50 p-3 rounded-xl border border-red-200 italic">
                                "{declineReason}"
                              </p>
                              <p className="text-xs text-gray-600 mt-3">
                                The customer provided this reason when declining the proposal.
                              </p>
                            </div>
                          </div>
                        );
                      }
                    } catch (e) {
                      // If notes is not valid JSON or doesn't contain decline_reason, don't show anything
                      return null;
                    }
                    return null;
                  })()}

                  {/* Proposal History */}
                  <div className="bg-white rounded-xl border border-gray-300 overflow-hidden">
                    <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 cursor-pointer hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation"
                         onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                         style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <h3 className="text-base font-semibold text-gray-900">History</h3>
                          {proposalEvents.length > 0 && (
                            <span className="bg-blue-100 text-blue-700 rounded-full px-2.5 py-0.5 text-xs font-medium">
                              {proposalEvents.length}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center">
                          {isHistoryExpanded ? (
                            <ChevronUp className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                          )}
                        </div>
                      </div>
                    </div>
                    {isHistoryExpanded && (
                      <div className="p-4">
                        {loadingEvents ? (
                          <div className="text-center py-8">
                            <div className="inline-block h-8 w-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
                            <p className="text-sm text-gray-600 font-medium">Loading history...</p>
                          </div>
                        ) : proposalEvents.length === 0 ? (
                          <div>
                            <p className="text-sm text-gray-600 italic">No history records available yet.</p>
                            <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-300">
                              <p className="text-sm text-gray-800 font-semibold mb-2">Diagnostic Info:</p>
                              <p className="text-xs text-gray-600 mb-1">Proposal ID: {localProposal.id}</p>
                              <p className="text-xs text-gray-600 mb-1">Proposal Status: {localProposal.status}</p>
                              <p className="text-xs text-gray-600 mb-3">Business ID: {localProposal.business_id}</p>
                              <div className="flex flex-col sm:flex-row gap-2">
                                <button 
                                  onClick={() => localProposal?.id && loadProposalEvents(localProposal.id)}
                                  className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                                >
                                  Retry Load Events
                                </button>
                                <button 
                                  onClick={createTestEvent}
                                  className="flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 active:bg-gray-800 transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm text-sm font-semibold"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  Create Test Event
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="max-h-64 overflow-y-auto space-y-3">
                            {proposalEvents.map((event) => {
                              const eventInfo = getEventTypeInfo(event.event_type);
                              
                              // Determine background color based on event type
                              let bgColorClass = "bg-gray-50";
                              if (event.event_type === 'sent_sms' || event.event_type === 'sent_email' || event.event_type === 'url_copied') {
                                bgColorClass = "bg-blue-50";
                              } else if (event.event_type === 'offer_approved') {
                                bgColorClass = "bg-green-50";
                              } else if (event.event_type === 'offer_declined') {
                                bgColorClass = "bg-red-50";
                              } else if (event.event_type === 'viewed') {
                                bgColorClass = "bg-purple-50";
                              } else if (event.event_type === 'test_event') {
                                bgColorClass = "bg-gray-50";
                              }
                              
                              return (
                                <div 
                                  key={event.id} 
                                  className={`rounded-xl border border-gray-300 p-4 ${bgColorClass} hover:shadow-sm transition-shadow`}
                                >
                                    <div className="flex">
                                    <div className="mr-3 mt-1 flex-shrink-0">
                                        {eventInfo.icon}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                      <div className="flex justify-between items-start mb-2">
                                        <span className={`text-sm font-bold ${eventInfo.color} truncate mr-2`}>{eventInfo.label}</span>
                                        <span className="text-xs text-gray-600 whitespace-nowrap bg-white px-2 py-1 rounded-lg border border-gray-200 flex-shrink-0 font-medium">
                                            {new Date(event.event_timestamp).toLocaleTimeString('en-US', {
                                              hour: '2-digit', 
                                              minute: '2-digit',
                                              hour12: true
                                            })}
                                          </span>
                                        </div>
                                        
                                      {/* Enhanced event content */}
                                      <div className="text-sm">
                                          {event.message && (
                                          <p className="text-gray-700 mb-2">{event.message}</p>
                                          )}
                                          
                                        {/* Enhanced metadata display */}
                                          {event.metadata && Object.keys(event.metadata).length > 0 && (
                                          <div className="text-sm text-gray-600">
                                              {event.event_type === 'sent_sms' && event.metadata.to_phone && (
                                                <div className="flex items-center">
                                                <Phone className="h-4 w-4 mr-2 text-blue-600" />
                                                <span className="font-medium">{event.metadata.to_phone}</span>
                                                </div>
                                              )}
                                              {event.event_type === 'sent_email' && event.metadata.to_email && (
                                                <div className="flex items-center">
                                                <Mail className="h-4 w-4 mr-2 text-blue-600" />
                                                <span className="font-medium">{event.metadata.to_email}</span>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Payment History */}
                  {proposalPayments.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-300 overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                        <h3 className="text-base font-semibold text-gray-900">Payment History</h3>
                      </div>
                      <div className="divide-y divide-gray-200">
                        {proposalPayments.map((payment) => {
                          const isDeposit = payment.payment_type === 'deposit';
                          const isCompleted = payment.status === 'completed';
                          const isPending = payment.status === 'pending';
                          
                          let statusConfig = {
                            color: 'text-gray-600',
                            bgColor: 'bg-gray-50',
                            borderColor: 'border-gray-200',
                            label: payment.status || 'Unknown'
                          };
                          
                          if (isCompleted) {
                            statusConfig = {
                              color: 'text-green-600',
                              bgColor: 'bg-green-50',
                              borderColor: 'border-green-200',
                              label: 'Completed'
                            };
                          } else if (isPending) {
                            statusConfig = {
                              color: 'text-yellow-600',
                              bgColor: 'bg-yellow-50',
                              borderColor: 'border-yellow-200',
                              label: payment.stripe_checkout_session_id ? 'Pending Stripe Pay' : 'Pending'
                            };
                          } else if (payment.status === 'failed') {
                            statusConfig = {
                              color: 'text-red-600',
                              bgColor: 'bg-red-50',
                              borderColor: 'border-red-200',
                              label: 'Failed'
                            };
                          }
                          
                          return (
                            <div key={payment.id} className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-sm font-medium ${statusConfig.color}`}>
                                      {statusConfig.label}
                                    </span>
                                    {isDeposit && (
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                        Deposit
                                      </span>
                                    )}
                                    {payment.method && (
                                      <span className="text-xs text-gray-500">
                                        • {payment.method.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                                      </span>
                                    )}
                                  </div>
                                  {payment.notes && (
                                    <p className="text-xs text-gray-500 mt-1">{payment.notes}</p>
                                  )}
                                  <p className="text-xs text-gray-400 mt-1">
                                    {new Date(payment.created_at).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                                <div className="text-right">
                                  <div className={`text-base font-semibold ${isCompleted ? 'text-gray-900' : statusConfig.color}`}>
                                    ${(payment.amount || 0).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {proposalAmountPaid > 0 && (
                        <div className="bg-gray-50 border-t border-gray-200 px-4 py-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">Total Paid</span>
                            <span className="text-base font-bold text-gray-900">${proposalAmountPaid.toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Proposal Photos */}
                  {proposalPhotos.length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-300 overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
                        <h3 className="text-base font-semibold text-gray-900">Photos</h3>
                      </div>
                      <div className="p-4">
                        <div className="grid grid-cols-3 gap-3">
                          {proposalPhotos.map((photo) => {
                            const { data: { publicUrl } } = supabase.storage
                              .from('user-uploads')
                              .getPublicUrl(photo.file_path);
                            return (
                              <div key={photo.id} className="relative group aspect-square">
                                <img
                                  src={publicUrl}
                                  alt={photo.file_name}
                                  className="w-full h-full object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => window.open(publicUrl, '_blank')}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Offers */}
                  {localProposal.offers?.map((offer, offerIndex) => {
                    // Determine if this offer should be greyed out
                    const isProposalApproved = localProposal.status === 'approved' || localProposal.status === 'booked';
                    const isProposalDeclined = localProposal.status === 'declined';
                    const isOfferSelected = offer.status === 'approved';
                    const isOfferGreyedOut = (isProposalApproved && !isOfferSelected) || isProposalDeclined;
                    
                    return (
                      <div 
                        key={offer.id} 
                        className={`bg-white rounded-xl border border-gray-300 overflow-hidden transition-all ${
                          isOfferGreyedOut 
                            ? 'opacity-60 border-gray-200' 
                            : isOfferSelected 
                              ? 'border-green-300 ring-2 ring-green-200' 
                              : 'border-gray-200'
                        } ${draggedOfferIndex === offerIndex ? 'opacity-50' : ''} ${draggedOfferIndex !== null && draggedOfferIndex !== offerIndex ? 'border-blue-300' : ''}`}
                        draggable={!readOnly && localProposal.offers && localProposal.offers.length > 1}
                        onDragStart={(e) => {
                          setDraggedOfferIndex(offerIndex);
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/html', offerIndex.toString());
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = 'move';
                        }}
                        onDrop={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const draggedIndex = parseInt(e.dataTransfer.getData('text/html'));
                          const targetIndex = offerIndex;
                          
                          if (draggedIndex !== targetIndex && draggedIndex !== null && localProposal.offers) {
                            const newOffers = [...localProposal.offers];
                            const [removed] = newOffers.splice(draggedIndex, 1);
                            newOffers.splice(targetIndex, 0, removed);
                            
                            // Update local state
                            setLocalProposal({
                              ...localProposal,
                              offers: newOffers
                            });
                            
                            // Update order in database
                            try {
                              // Update order_index for each offer
                              for (let i = 0; i < newOffers.length; i++) {
                                await supabase
                                  .from('proposal_offers')
                                  .update({ order_index: i })
                                  .eq('id', newOffers[i].id);
                              }
                              
                              // Refresh proposal to get updated data
                              if (onUpdate && localProposal.id) {
                                const updatedProposal = await getProposal(localProposal.id);
                                onUpdate(updatedProposal);
                              }
                              
                              toast.success('Offer order updated');
                            } catch (error) {
                              console.error('Error updating offer order:', error);
                              toast.error('Failed to update offer order');
                              // Revert local state on error
                              setLocalProposal(proposal);
                            }
                          }
                          setDraggedOfferIndex(null);
                        }}
                        onDragEnd={() => {
                          setDraggedOfferIndex(null);
                        }}
                      >
                        <div 
                          className={`px-4 py-3 border-b flex justify-between items-center cursor-pointer hover:bg-gray-100 active:bg-gray-200 transition-colors touch-manipulation ${
                            isOfferGreyedOut 
                              ? 'bg-gray-50 border-gray-200' 
                              : isOfferSelected 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-gray-50 border-gray-200'
                          }`}
                          onClick={() => {
                            setExpandedOffers(prev => {
                              const newSet = new Set(prev);
                              if (newSet.has(offer.id)) {
                                newSet.delete(offer.id);
                              } else {
                                newSet.add(offer.id);
                              }
                              return newSet;
                            });
                          }}
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="flex items-center space-x-3 flex-1">
                            {!readOnly && localProposal.offers && localProposal.offers.length > 1 && (
                              <div 
                                className="cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0 touch-none"
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <GripVertical className="h-5 w-5" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                            <h3 className={`text-base font-bold ${
                              isOfferGreyedOut ? 'text-gray-600' : isOfferSelected ? 'text-green-800' : 'text-gray-900'
                            }`}>
                              {offer.title}
                            </h3>
                              {offer.show_customer_address && fullCustomer?.address?.trim() && (
                                <p className={`text-xs mt-0.5 ${
                                  isOfferGreyedOut ? 'text-gray-500' : 'text-gray-600'
                                }`}>
                                  {fullCustomer.address.trim()}
                                </p>
                              )}
                              {offer.deposit_percentage && offer.deposit_percentage > 0 && (
                                <p className={`text-xs mt-0.5 ${
                                  isOfferGreyedOut ? 'text-gray-500' : 'text-gray-800'
                                }`}>
                                  Deposit Required: {offer.deposit_percentage}%
                                </p>
                              )}
                            </div>
                            {offer.status === 'approved' && (
                              <span className="px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800">
                                Selected by Customer
                              </span>
                            )}
                            {offer.status === 'declined' && (
                              <span className="px-3 py-1 text-sm font-medium rounded-full bg-gray-100 text-gray-700">
                                Not Selected
                              </span>
                            )}
                            {localProposal.status === 'declined' && (
                              <span className="px-3 py-1 text-sm font-medium rounded-full bg-red-100 text-red-700">
                                Declined by Customer
                              </span>
                            )}
                            {offer.services && offer.services.length > 0 && (() => {
                              const selectedCount = offer.status === 'approved'
                                ? offer.services.filter((s: any) => {
                                    const selectable = s.isSelectable || s.is_selectable;
                                    return !selectable || (selectable && s.is_selected);
                                  }).length
                                : offer.services.length;
                              return (
                                <span className="text-xs text-gray-500">
                                  {selectedCount} {selectedCount === 1 ? 'service' : 'services'}
                                </span>
                              );
                            })()}
                          </div>
                          <div className="flex items-center">
                            {expandedOffers.has(offer.id) ? (
                              <ChevronUp className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-gray-500 transition-transform duration-200" />
                            )}
                          </div>
                        </div>
                        {offer.status === 'pending' && !readOnly && localProposal.status !== 'declined' && (
                          <div className="px-4 py-3 flex gap-2">
                            <button
                              onClick={() => handleApproveOffer(offer)}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <CheckCircle className="h-4 w-4 mr-1.5" />
                              Approve
                            </button>
                            <button
                              onClick={() => handleDeclineOffer(offer)}
                              className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-red-700 bg-red-50 hover:bg-red-100 active:bg-red-200 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <X className="h-4 w-4 mr-1.5" />
                              Decline
                            </button>
                          </div>
                        )}
                        {expandedOffers.has(offer.id) && (
                          <div className="divide-y divide-gray-200">
                            {offer.services?.map((service) => {
                            // Determine service styling based on selection state
                            const isServiceSelectable = (service as any).isSelectable || (service as any).is_selectable;
                            const isServiceSelected = (service as any).is_selected;
                            const shouldGreyOutService = isOfferGreyedOut || (isServiceSelectable && !isServiceSelected && isProposalApproved);
                            const shouldHighlightService = isServiceSelectable && isServiceSelected && isProposalApproved;

                            return (
                              <div 
                                key={service.id} 
                                className={`p-4 transition-all ${
                                  shouldGreyOutService 
                                    ? 'opacity-50 bg-gray-50' 
                                    : shouldHighlightService 
                                      ? 'bg-green-50 border-l-4 border-green-500' 
                                      : ''
                                }`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-start">
                                <div className="flex-1">
                                      <div className="flex items-center flex-wrap gap-2">
                                        <span className={`text-base font-bold ${
                                          shouldGreyOutService ? 'text-gray-500' : shouldHighlightService ? 'text-green-800' : 'text-gray-900'
                                        }`}>
                                          {service.name}
                                        </span>
                                        {((service as any).pricing_type === 'per_sqft' || (service as any).pricing_type === 'per_unit') && (
                                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-blue-50 text-blue-600">
                                            {(service as any).pricing_type === 'per_sqft' ? 'PER SQ FT' : 'PER UNIT'}
                                          </span>
                                        )}
                                        {isServiceSelectable && (
                                          <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                                            shouldGreyOutService 
                                              ? 'bg-gray-100 text-gray-500' 
                                              : shouldHighlightService 
                                                ? 'bg-green-100 text-green-800' 
                                                : 'bg-blue-100 text-blue-800'
                                          }`}>
                                            {isProposalApproved 
                                              ? (isServiceSelected ? 'Selected' : 'Not Selected')
                                              : 'Optional'}
                                          </span>
                                        )}
                                  </div>
                                      <div className={`text-sm mt-2 ${shouldGreyOutService ? 'text-gray-400' : 'text-gray-600'}`}>
                                    <RichTextDescription 
                                      description={service.description} 
                                          className={`prose-sm ${
                                            shouldGreyOutService 
                                              ? 'text-gray-400 prose-headings:text-gray-400 prose-strong:text-gray-400 prose-em:text-gray-300' 
                                              : 'text-gray-600 prose-headings:text-gray-700 prose-strong:text-gray-700 prose-em:text-gray-500'
                                          }`}
                                    />
                                  </div>
                                </div>
                              </div>
                                                                <div className={`ml-4 ${
                                shouldGreyOutService ? 'text-gray-500' : shouldHighlightService ? 'text-green-800' : 'text-gray-900'
                              }`}>
                            {(() => {
                              const discount = service.discount_amount && service.discount_amount > 0 ? service.discount_amount : 0;
                              const net = service.price - discount;
                              const compareAt = (service as any).compare_at_price;
                              const hasCompare = compareAt != null && Number(compareAt) > service.price;
                              const struck = hasCompare ? Number(compareAt) : (discount > 0 ? service.price : null);
                              if (struck == null) {
                                return (
                                  <div className="text-base font-bold">
                                    ${service.price.toLocaleString()}
                                  </div>
                                );
                              }
                              return (
                                <div className="text-right">
                                  <div className="text-sm text-gray-500 line-through">
                                    ${struck.toLocaleString()}
                                  </div>
                                  <div className="text-base font-bold text-green-600">
                                    ${net.toLocaleString()}
                                  </div>
                                  {discount > 0 && (
                                    <div className="text-xs text-gray-500">
                                      {((service as any).discount_type || service.discountType) === 'percentage' ? `${((service as any).discount_value || service.discountValue)}% off` : `$${service.discount_amount} off`}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                            </div>
                                {/* Per-unit/sqft quantity breakdown */}
                                {((service as any).pricing_type === 'per_sqft' || (service as any).pricing_type === 'per_unit') && (service as any).quantity > 0 && (
                                  <div className={`text-xs mb-2 ${shouldGreyOutService ? 'text-gray-400' : 'text-gray-500'}`}>
                                    <span className="font-medium">
                                      {((service as any).quantity || 0).toLocaleString()} {(service as any).pricing_type === 'per_sqft' ? 'sq ft' : 'units'} × ${((service as any).unit_price || 0).toFixed(2)}/{(service as any).pricing_type === 'per_sqft' ? 'sqft' : 'unit'}
                                    </span>
                                    {(service as any).measurement_id && (
                                      <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 bg-green-50 border border-green-200 rounded text-green-700 text-[11px] font-medium">
                                        📐 Measurement linked
                                      </span>
                                    )}
                                  </div>
                                )}

                                <div className={`flex items-center justify-between text-sm ${
                                  shouldGreyOutService ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                              <div className="flex items-center">
                                    <Clock className="h-4 w-4 mr-2" />
                                    <span className="font-medium">
                                {service.duration_minutes >= 1440 
                                  ? `${Math.round(service.duration_minutes / 1440)} days`
                                  : `${Math.round(service.duration_minutes / 60)} hours`
                                }
                                    </span>
                              </div>
                              {selectedBusiness?.industry !== 'Home Services' && (
                              <div className="flex items-center">
                                    <span className="font-medium">
                                {service.vehicle_make && service.vehicle_model 
                                  ? `${service.vehicle_make} ${service.vehicle_model}`
                                  : 'Vehicle details not available'
                                }
                                    </span>
                              </div>
                              )}
                            </div>
                                {(() => {
                                  const lk = ((service as any).line_key as string | undefined)
                                    || lineKeyByServiceId[String((service as any).id)]
                                    || lineKeyByServiceId[String((service as any).service_id)]
                                    || lineKeyByServiceId[`name:${service.name}`];
                                  const photos = lk ? (servicePhotosByKey[lk] || []) : [];
                                  if (photos.length === 0) return null;
                                  const bust = (ph: ProposalServicePhoto) => {
                                    const u = proposalServicePhotoUrl(ph.file_path);
                                    return photoBust[ph.id] ? `${u}${u.includes('?') ? '&' : '?'}t=${photoBust[ph.id]}` : u;
                                  };
                                  return (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                      {photos.map((p, pi) => (
                                        <div key={p.id} className="relative h-16 w-16 group">
                                          <img
                                            src={bust(p)}
                                            alt=""
                                            onClick={() => openImageViewer({
                                              images: photos.map((ph) => ({ src: bust(ph), caption: service.name })),
                                              index: pi,
                                              onSaveEdit: async (idx, blob) => {
                                                const target = photos[idx];
                                                if (!target) return;
                                                try {
                                                  await supabase.storage.from('user-uploads').upload(target.file_path, blob, { upsert: true, contentType: 'image/jpeg', cacheControl: '0' });
                                                  setPhotoBust((prev) => ({ ...prev, [target.id]: Date.now() }));
                                                } catch (e) {
                                                  console.error('Error saving edited photo:', e);
                                                }
                                              },
                                            })}
                                            className="h-full w-full object-cover rounded-lg border border-gray-200 bg-gray-50 cursor-pointer"
                                          />
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                          </div>
                            );
                          })}
                          </div>
                        )}

                        {/* Products Section */}
                        {(offer as any).products && (offer as any).products.length > 0 && (
                          <div className="divide-y divide-gray-200">
                            {(offer as any).products.map((product: any) => {
                            // Determine product styling based on selection state
                            const isProductSelectable = product.isSelectable;
                            const isProductSelected = product.is_selected;
                            const shouldGreyOutProduct = isOfferGreyedOut || (isProductSelectable && !isProductSelected && isProposalApproved);
                            const shouldHighlightProduct = isProductSelectable && isProductSelected && isProposalApproved;

                            return (
                              <div 
                                key={product.id} 
                                className={`p-6 transition-all border-l-4 border-green-500 ${
                                  shouldGreyOutProduct 
                                    ? 'opacity-50 bg-gray-50' 
                                    : shouldHighlightProduct 
                                      ? 'bg-green-50 border-l-4 border-green-500' 
                                      : 'bg-green-50/30'
                                }`}
                              >
                                <div className="flex items-start justify-between mb-3">
                                  <div className="flex items-start">
                                    <div className="flex-shrink-0 mr-4">
                                      <ProductIcon product={product} />
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center flex-wrap gap-2">
                                        <span className={`text-base font-bold ${
                                          shouldGreyOutProduct ? 'text-gray-500' : shouldHighlightProduct ? 'text-green-800' : 'text-gray-900'
                                        }`}>
                                          {product.name}
                                        </span>
                                        {isProductSelectable && (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                            Optional
                                          </span>
                                        )}
                                      </div>
                                      
                                      {product.discount_amount > 0 && (
                                        <div className={`text-sm mt-1 ${
                                          shouldGreyOutProduct ? 'text-gray-500' : 'text-green-600'
                                        }`}>
                                          Discount: -${product.discount_amount.toLocaleString()}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className="text-right">
                                    <div className={`text-lg font-bold ${
                                      shouldGreyOutProduct ? 'text-gray-500' : shouldHighlightProduct ? 'text-green-800' : 'text-gray-900'
                                    }`}>
                                      ${(product.price * product.quantity).toLocaleString()}
                                    </div>
                                    <div className={`text-sm mt-1 ${
                                      shouldGreyOutProduct ? 'text-gray-500' : shouldHighlightProduct ? 'text-green-700' : 'text-gray-600'
                                    }`}>
                                      Quantity: {product.quantity} × ${product.price.toLocaleString()}
                                    </div>
                                    {selectedBusiness?.industry !== 'Home Services' && (
                                    <div className={`text-sm mt-2 flex items-center justify-end ${
                                      shouldGreyOutProduct ? 'text-gray-500' : shouldHighlightProduct ? 'text-green-700' : 'text-gray-600'
                                    }`}>
                                      <span className="font-medium">
                                        {product.vehicle_make && product.vehicle_model 
                                          ? `${product.vehicle_make} ${product.vehicle_model}`
                                          : 'Vehicle details not available'
                                        }
                                      </span>
                                    </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                            })}
                          </div>
                        )}
                        <div className={`p-4 ${
                          isOfferGreyedOut 
                            ? 'bg-gray-50' 
                            : isOfferSelected 
                              ? 'bg-green-50' 
                              : 'bg-gray-50'
                        }`}>
                          <div className="space-y-3">
                            {(() => {
                              // For approved offers, only count selected services/products
                              const activeServices = offer.status === 'approved'
                                ? (offer.services || []).filter((s: any) => {
                                    const selectable = s.isSelectable || s.is_selectable;
                                    return !selectable || (selectable && s.is_selected);
                                  })
                                : (offer.services || []);
                              
                              const activeProducts = offer.status === 'approved'
                                ? ((offer as any).products || []).filter((p: any) => {
                                    const selectable = p.isSelectable || p.is_selectable;
                                    return !selectable || (selectable && p.is_selected);
                                  })
                                : ((offer as any).products || []);

                              const serviceSubtotal = activeServices.reduce((sum: number, service: any) => sum + service.price, 0);
                              const serviceDiscount = activeServices.reduce((sum: number, service: any) => sum + (service.discount_amount || 0), 0);
                              
                              const productSubtotal = activeProducts.reduce((sum: number, product: any) => sum + (product.price * product.quantity), 0);
                              const productDiscount = activeProducts.reduce((sum: number, product: any) => sum + (product.discount_amount || 0), 0);
                              
                              const subtotal = serviceSubtotal + productSubtotal;
                              const totalDiscount = serviceDiscount + productDiscount;
                              const discountedSubtotal = subtotal - totalDiscount;
                              const businessTax = Math.round((discountedSubtotal * combinedBusinessRate) * 100) / 100;

                              const addlTaxMap = new Map<string, { name: string; rate: number; amount: number }>();
                              for (const svc of activeServices) {
                                const svcId = (svc as any).service_id;
                                const svcNet = (svc.price || 0) - ((svc as any).discount_amount || 0);
                                for (const atx of serviceAdditionalTaxes.filter(t => t.service_id === svcId)) {
                                  const amt = svcNet * atx.rate / 100;
                                  const key = `${atx.name}_${atx.rate}`;
                                  if (addlTaxMap.has(key)) {
                                    addlTaxMap.get(key)!.amount += amt;
                                  } else {
                                    addlTaxMap.set(key, { name: atx.name, rate: atx.rate, amount: amt });
                                  }
                                }
                              }
                              const addlTaxLines = Array.from(addlTaxMap.values()).map(t => ({ ...t, amount: Math.round(t.amount * 100) / 100 }));
                              const addlTaxTotal = addlTaxLines.reduce((s, t) => s + t.amount, 0);
                              const totalTax = businessTax + addlTaxTotal;
                              const total = Math.round((discountedSubtotal + totalTax) * 100) / 100;

                              return (
                                <>
                                  <div className={`flex justify-between text-sm ${
                                    isOfferGreyedOut ? 'text-gray-600' : isOfferSelected ? 'text-green-700' : 'text-gray-700'
                                  }`}>
                                    <span className="font-medium">Subtotal</span>
                                    <span className="font-bold">${subtotal.toLocaleString()}</span>
                                  </div>
                                  
                                  {totalDiscount > 0 && (
                                    <div className={`flex justify-between text-sm ${
                                      isOfferGreyedOut ? 'text-gray-600' : isOfferSelected ? 'text-green-700' : 'text-green-600'
                                    }`}>
                                      <span className="font-medium">Discount</span>
                                      <span className="font-bold">- ${totalDiscount.toLocaleString()}</span>
                                    </div>
                                  )}
                                  
                                  {businessTaxRates.map((rate, ri) => (
                                    <div key={ri} className={`flex justify-between text-sm ${
                                      isOfferGreyedOut ? 'text-gray-600' : isOfferSelected ? 'text-green-700' : 'text-gray-700'
                                    }`}>
                                      <span className="font-medium">{rate.name} ({rate.rate}%)</span>
                                      <span className="font-bold">+ ${(Math.round((discountedSubtotal * rate.rate / 100) * 100) / 100).toLocaleString()}</span>
                                    </div>
                                  ))}
                                  {addlTaxLines.map((line, li) => (
                                    <div key={`addl-${li}`} className={`flex justify-between text-sm ${
                                      isOfferGreyedOut ? 'text-gray-600' : isOfferSelected ? 'text-green-700' : 'text-gray-700'
                                    }`}>
                                      <span className="font-medium">{line.name} ({line.rate}%)</span>
                                      <span className="font-bold">+ ${line.amount.toLocaleString()}</span>
                                    </div>
                                  ))}
                                  
                                  <div className={`flex justify-between text-lg font-bold pt-3 border-t ${
                                    isOfferGreyedOut 
                                      ? 'text-gray-700 border-gray-300' 
                                      : isOfferSelected 
                                        ? 'text-green-800 border-green-300' 
                                        : 'text-gray-900 border-gray-200'
                                  }`}>
                                    <span>Total</span>
                                    <span>${total.toLocaleString()}</span>
                                  </div>
                                  
                                  {/* Deposit Information */}
                                  {offer.deposit_percentage && offer.deposit_percentage > 0 && (() => {
                                    // Calculate deposit amount from percentage
                                    // For approved offers, calculate based on selected services only
                                    // For other offers, calculate based on all services
                                    let calculatedTotal = total;
                                    
                                    if (offer.status === 'approved' && offer.services) {
                                      // Recalculate total based on selected services only
                                      const selectedServices = offer.services.filter((service: any) => {
                                        const isSelectable = service.isSelectable || false;
                                        const isSelected = (service as any).is_selected === true;
                                        return !isSelectable || (isSelectable && isSelected);
                                      });
                                      
                                      const selectedServiceSubtotal = selectedServices.reduce((sum: number, service: any) => {
                                        const price = service.price || 0;
                                        const discountAmount = service.discount_amount || 0;
                                        return sum + price - discountAmount;
                                      }, 0);
                                      
                                      const selectedProductSubtotal = ((offer as any).products || []).reduce((sum: number, product: any) => {
                                        const isSelectable = product.isSelectable || false;
                                        const isSelected = product.is_selected === true;
                                        if (isSelectable && !isSelected) return sum;
                                        const price = product.price || 0;
                                        const quantity = product.quantity || 1;
                                        return sum + (price * quantity);
                                      }, 0);
                                      
                                      const selectedSubtotal = selectedServiceSubtotal + selectedProductSubtotal;
                                      const selectedBizTax = Math.round((selectedSubtotal * combinedBusinessRate) * 100) / 100;
                                      let selectedAddlTax = 0;
                                      for (const svc of selectedServices) {
                                        const svcId = (svc as any).service_id;
                                        const svcNet = (svc.price || 0) - ((svc as any).discount_amount || 0);
                                        for (const atx of serviceAdditionalTaxes.filter(t => t.service_id === svcId)) {
                                          selectedAddlTax += svcNet * atx.rate / 100;
                                        }
                                      }
                                      calculatedTotal = Math.round((selectedSubtotal + selectedBizTax + selectedAddlTax) * 100) / 100;
                                    }
                                    
                                    const depositAmount = Math.round((calculatedTotal * offer.deposit_percentage / 100) * 100) / 100;
                                    const remainingAfterDeposit = calculatedTotal - depositAmount;
                                    
                                    // Check if deposit has been paid
                                    const completedDepositPayments = proposalPayments.filter(
                                      p => p.payment_type === 'deposit' && p.status === 'completed'
                                    );
                                    const depositPaid = completedDepositPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
                                    const hasDepositPaid = depositPaid > 0;
                                    
                                    return (
                                      <>
                                        {hasDepositPaid ? (
                                          <>
                                            <div className={`flex justify-between text-sm font-medium pt-2 border-t ${
                                              isOfferGreyedOut 
                                                ? 'text-gray-600 border-gray-300' 
                                                : isOfferSelected 
                                                  ? 'text-green-700 border-green-300' 
                                                  : 'text-green-700 border-gray-200'
                                            }`}>
                                              <span>Deposit Paid</span>
                                              <span>${depositPaid.toFixed(2)}</span>
                                            </div>
                                            <div className={`flex justify-between text-xs font-medium ${
                                              isOfferGreyedOut ? 'text-gray-500' : 'text-gray-700'
                                            }`}>
                                              <span>Remaining Balance</span>
                                              <span>${(calculatedTotal - depositPaid).toLocaleString()}</span>
                                  </div>
                                          </>
                                        ) : (
                                          <>
                                            <div className={`flex justify-between text-sm font-medium pt-2 border-t ${
                                              isOfferGreyedOut 
                                                ? 'text-gray-600 border-gray-300' 
                                                : isOfferSelected 
                                                  ? 'text-gray-800 border-green-300' 
                                                  : 'text-gray-800 border-gray-200'
                                            }`}>
                                              <span>Deposit Required ({offer.deposit_percentage}%)</span>
                                              <span>${depositAmount.toLocaleString()}</span>
                                            </div>
                                            <div className={`flex justify-between text-xs font-medium ${
                                              isOfferGreyedOut ? 'text-gray-500' : 'text-gray-500'
                                            }`}>
                                              <span>Remaining After Deposit</span>
                                              <span>${remainingAfterDeposit.toLocaleString()}</span>
                                            </div>
                                          </>
                                        )}
                                      </>
                                    );
                                  })()}
                                </>
                              );
                            })()}
                          </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                
                  {/* Decline All Button */}
                  {localProposal.status === 'pending' && (
                    <div className="mt-6">
                      <button
                        onClick={() => {
                          localProposal.offers?.forEach(offer => handleDeclineOffer(offer));
                        }}
                        disabled={loading || readOnly}
                        className="w-full inline-flex items-center justify-center px-6 py-4 text-lg font-semibold text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors"
                      >
                        <X className="h-6 w-6 mr-3" />
                        Decline All Offers
                      </button>
                    </div>
                  )}
              </div>
              )}

              {/* Invoice View */}
              {activeView === 'invoice' && (
                <div className={`${isMobile ? 'p-4' : 'p-6'}`} style={{
                  paddingBottom: isMobile ? 'calc(1rem + env(safe-area-inset-bottom, 0px))' : '1rem'
                }}>
                  {isLoadingInvoiceSettings ? (
                    <div className="flex items-center justify-center py-16">
                      <div className="text-center">
                        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-gray-500 font-medium">Loading invoice...</p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
                      {/* Invoice Header */}
                      <div className="p-6 border-b border-gray-100" style={{ backgroundColor: invoiceSettings?.header_background || '#F8FAFC' }}>
                        <div className="flex flex-col sm:flex-row justify-between items-start space-y-4 sm:space-y-0">
                          <div>
                            {invoiceSettings?.include_logo && invoiceSettings?.logo_url && (
                              <div className="w-16 h-16 bg-gray-200 rounded-lg mb-4 flex items-center justify-center overflow-hidden">
                                <img 
                                  src={invoiceSettings.logo_url} 
                                  alt="Company Logo" 
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            )}
                            <h1 className="text-2xl font-bold" style={{ color: invoiceSettings?.primary_color || '#3B82F6' }}>
                              ESTIMATE
                            </h1>
                            <p className="text-sm text-gray-500 mt-1">#{localProposal.proposal_number || localProposal.id?.slice(0, 8)}</p>
                          </div>
                          <div className="text-left sm:text-right">
                            <h2 className="font-bold text-gray-900 text-lg">{invoiceSettings?.company_name || selectedBusiness?.name}</h2>
                            {invoiceSettings?.company_address && (
                              <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{invoiceSettings.company_address}</p>
                            )}
                            {invoiceSettings?.company_phone && (
                              <p className="text-sm text-gray-600">{invoiceSettings.company_phone}</p>
                            )}
                            {invoiceSettings?.company_email && (
                              <p className="text-sm text-gray-600">{invoiceSettings.company_email}</p>
                            )}
                            {selectedBusiness?.gst_number && (
                              <p className="text-sm text-gray-600 mt-1">GST: {selectedBusiness.gst_number}</p>
                            )}
                            {selectedBusiness?.pst_number && (
                              <p className="text-sm text-gray-600">PST: {selectedBusiness.pst_number}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Customer & Date Info */}
                      <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          <div>
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Bill To</h3>
                            <p className="font-semibold text-gray-900">
                              {localProposal.customer?.first_name} {localProposal.customer?.last_name}
                            </p>
                            {localProposal.customer?.email && (
                              <p className="text-sm text-gray-600">{localProposal.customer.email}</p>
                            )}
                            {localProposal.customer?.phone && (
                              <p className="text-sm text-gray-600">{localProposal.customer.phone}</p>
                            )}
                          </div>
                          <div className="sm:text-right">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Estimate Date</h3>
                            <p className="text-gray-900">
                              {new Date(localProposal.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                            {localProposal.expires_at && (
                              <>
                                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mt-3 mb-2">Valid Until</h3>
                                <p className="text-gray-900">
                                  {new Date(localProposal.expires_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Proposal Notes/Description */}
                      {localProposal.notes && (
                        <div className="p-6 border-b border-gray-100 bg-white">
                          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Notes</h3>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">{localProposal.notes}</p>
                        </div>
                      )}

                      {/* Line Items */}
                      <div className="p-6">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider pb-3">Description</th>
                              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider pb-3">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {(() => {
                              // Get the offer to display - approved offer if available, otherwise first offer
                              const displayOffer = localProposal.offers?.find(o => o.status === 'approved') || localProposal.offers?.[0];
                              if (!displayOffer) return null;

                              // Filter services - for approved offers, only show selected ones
                              const services = displayOffer.services?.filter(service => {
                                if (displayOffer.status !== 'approved') return true;
                                return (service as any).is_selected === true;
                              }) || [];

                              const products = (displayOffer as any).products?.filter((product: any) => {
                                if (displayOffer.status !== 'approved') return true;
                                return product.is_selected === true;
                              }) || [];

                              return (
                                <>
                                  {services.map((service) => (
                                    <tr key={service.id}>
                                      <td className="py-4 align-top">
                                        <p className="font-medium text-gray-900">{service.name}</p>
                                        {service.description && (
                                          <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{service.description}</p>
                                        )}
                                        {((service as any).pricing_type === 'per_sqft' || (service as any).pricing_type === 'per_unit') && (service as any).quantity > 0 && (
                                          <p className="text-xs text-gray-500 mt-1">
                                            {((service as any).quantity || 0).toLocaleString()} {(service as any).pricing_type === 'per_sqft' ? 'sq ft' : 'units'} × ${((service as any).unit_price || 0).toFixed(2)}/{(service as any).pricing_type === 'per_sqft' ? 'sqft' : 'unit'}
                                          </p>
                                        )}
                                        {service.vehicle_make && service.vehicle_model && (
                                          <p className="text-xs text-gray-500 mt-1">{service.vehicle_make} {service.vehicle_model}</p>
                                        )}
                                        {(() => {
                                          const lk = ((service as any).line_key as string | undefined)
                                            || lineKeyByServiceId[String((service as any).id)]
                                            || lineKeyByServiceId[String((service as any).service_id)]
                                            || lineKeyByServiceId[`name:${service.name}`];
                                          const photos = lk ? (servicePhotosByKey[lk] || []) : [];
                                          if (photos.length === 0) return null;
                                          const bust = (ph: ProposalServicePhoto) => {
                                            const u = proposalServicePhotoUrl(ph.file_path);
                                            return photoBust[ph.id] ? `${u}${u.includes('?') ? '&' : '?'}t=${photoBust[ph.id]}` : u;
                                          };
                                          return (
                                            <div className="mt-2 flex flex-wrap gap-2">
                                              {photos.map((p, pi) => (
                                                <div key={p.id} className="relative h-14 w-14 group">
                                                  <img
                                                    src={bust(p)}
                                                    alt=""
                                                    onClick={() => openImageViewer({
                                                      images: photos.map((ph) => ({ src: bust(ph), caption: service.name })),
                                                      index: pi,
                                                      onSaveEdit: async (idx, blob) => {
                                                        const target = photos[idx];
                                                        if (!target) return;
                                                        try {
                                                          await supabase.storage.from('user-uploads').upload(target.file_path, blob, { upsert: true, contentType: 'image/jpeg', cacheControl: '0' });
                                                          setPhotoBust((prev) => ({ ...prev, [target.id]: Date.now() }));
                                                        } catch (e) {
                                                          console.error('Error saving edited photo:', e);
                                                        }
                                                      },
                                                    })}
                                                    className="h-full w-full object-cover rounded-lg border border-gray-200 bg-gray-50 cursor-pointer"
                                                  />
                                                  <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteServicePhoto(p.id, lk!); }}
                                                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-gray-900/90 text-white flex items-center justify-center shadow-md z-20 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    title="Delete photo"
                                                  >
                                                    <X className="h-3.5 w-3.5" />
                                                  </button>
                                                </div>
                                              ))}
                                            </div>
                                          );
                                        })()}
                                      </td>
                                      <td className="py-4 text-right align-top">
                                        {(() => {
                                          const discount = service.discount_amount && service.discount_amount > 0 ? service.discount_amount : 0;
                                          const net = service.price - discount;
                                          const compareAt = (service as any).compare_at_price;
                                          const hasCompare = compareAt != null && Number(compareAt) > service.price;
                                          const struck = hasCompare ? Number(compareAt) : (discount > 0 ? service.price : null);
                                          if (struck == null) {
                                            return (<span className="font-medium text-gray-900">${service.price.toFixed(2)}</span>);
                                          }
                                          return (
                                            <div>
                                              <span className="text-sm text-gray-400 line-through block">${struck.toFixed(2)}</span>
                                              <span className="font-medium text-gray-900">${net.toFixed(2)}</span>
                                            </div>
                                          );
                                        })()}
                                      </td>
                                    </tr>
                                  ))}
                                  {products.map((product: any) => (
                                    <tr key={product.id}>
                                      <td className="py-4 align-top">
                                        <p className="font-medium text-gray-900">{product.name}</p>
                                        {product.quantity > 1 && (
                                          <p className="text-xs text-gray-500 mt-1">Qty: {product.quantity}</p>
                                        )}
                                      </td>
                                      <td className="py-4 text-right font-medium text-gray-900 align-top">
                                        ${(product.price * (product.quantity || 1)).toFixed(2)}
                                      </td>
                                    </tr>
                                  ))}
                                </>
                              );
                            })()}
                          </tbody>
                        </table>
                      </div>

                      {/* Totals */}
                      <div className="p-6 bg-gray-50 border-t border-gray-200">
                        <div className="flex justify-end">
                          <div className="w-full sm:w-72 space-y-2">
                            {(() => {
                              const displayOffer = localProposal.offers?.find(o => o.status === 'approved') || localProposal.offers?.[0];
                              if (!displayOffer) return null;

                              // Filter services and products
                              const services = displayOffer.services?.filter(service => {
                                if (displayOffer.status !== 'approved') return true;
                                return (service as any).is_selected === true;
                              }) || [];

                              const products = (displayOffer as any).products?.filter((product: any) => {
                                if (displayOffer.status !== 'approved') return true;
                                return product.is_selected === true;
                              }) || [];

                              // Calculate subtotal (before discounts)
                              const serviceSubtotal = services.reduce((sum, s) => sum + s.price, 0);
                              const productSubtotal = products.reduce((sum: number, p: any) => sum + (p.price * (p.quantity || 1)), 0);
                              const subtotal = serviceSubtotal + productSubtotal;

                              // Calculate total discounts
                              const totalDiscount = services.reduce((sum, s) => sum + (s.discount_amount || 0), 0);

                              // Calculate tax from business rates
                              const invoiceDiscountedSub = subtotal - totalDiscount;
                              const invoiceBizTax = Math.round((invoiceDiscountedSub * combinedBusinessRate) * 100) / 100;
                              let invoiceAddlTax = 0;
                              for (const svc of services) {
                                const svcId = (svc as any).service_id;
                                const svcNet = (svc.price || 0) - (svc.discount_amount || 0);
                                for (const atx of serviceAdditionalTaxes.filter(t => t.service_id === svcId)) {
                                  invoiceAddlTax += svcNet * atx.rate / 100;
                                }
                              }
                              invoiceAddlTax = Math.round(invoiceAddlTax * 100) / 100;
                              const totalTax = invoiceBizTax + invoiceAddlTax;

                              // Calculate final total
                              const total = Math.round((invoiceDiscountedSub + totalTax) * 100) / 100;

                              return (
                                <>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Subtotal</span>
                                    <span className="text-gray-900">${subtotal.toFixed(2)}</span>
                                  </div>
                                  {totalDiscount > 0 && (
                                    <div className="flex justify-between text-sm text-green-600">
                                      <span>Discount</span>
                                      <span>-${totalDiscount.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {invoiceSettings?.show_tax_breakdown && (() => {
                                    const discountedSub = subtotal - totalDiscount;
                                    const bizTax = Math.round((discountedSub * combinedBusinessRate) * 100) / 100;
                                    const invAddlMap = new Map<string, { name: string; rate: number; amount: number }>();
                                    for (const svc of services) {
                                      const svcId = (svc as any).service_id;
                                      const svcNet = (svc.price || 0) - (svc.discount_amount || 0);
                                      for (const atx of serviceAdditionalTaxes.filter(t => t.service_id === svcId)) {
                                        const amt = svcNet * atx.rate / 100;
                                        const key = `${atx.name}_${atx.rate}`;
                                        if (invAddlMap.has(key)) invAddlMap.get(key)!.amount += amt;
                                        else invAddlMap.set(key, { name: atx.name, rate: atx.rate, amount: amt });
                                      }
                                    }
                                    const invAddlLines = Array.from(invAddlMap.values()).map(t => ({ ...t, amount: Math.round(t.amount * 100) / 100 }));
                                    return (
                                      <>
                                        {businessTaxRates.map((rate, ri) => {
                                          const rateAmount = Math.round((discountedSub * rate.rate / 100) * 100) / 100;
                                          return rateAmount > 0 ? (
                                            <div key={ri} className="flex justify-between text-sm">
                                              <span className="text-gray-600">{rate.name} ({rate.rate}%)</span>
                                              <span className="text-gray-900">${rateAmount.toFixed(2)}</span>
                                            </div>
                                          ) : null;
                                        })}
                                        {invAddlLines.map((line, li) => (
                                          <div key={`inv-addl-${li}`} className="flex justify-between text-sm">
                                            <span className="text-gray-600">{line.name} ({line.rate}%)</span>
                                            <span className="text-gray-900">${line.amount.toFixed(2)}</span>
                                          </div>
                                        ))}
                                      </>
                                    );
                                  })()}
                                  <div className="flex justify-between pt-3 border-t border-gray-200">
                                    <span className="text-lg font-bold text-gray-900">Total</span>
                                    <span className="text-lg font-bold" style={{ color: invoiceSettings?.primary_color || '#3B82F6' }}>
                                      ${total.toFixed(2)}
                                    </span>
                                  </div>
                                  {displayOffer.deposit_percentage && displayOffer.deposit_percentage > 0 && (
                                    <div className="flex justify-between text-sm pt-2 border-t border-gray-100 mt-2">
                                      <span className="text-gray-600">Deposit Required ({displayOffer.deposit_percentage}%)</span>
                                      <span className="font-medium text-gray-900">${(total * displayOffer.deposit_percentage / 100).toFixed(2)}</span>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Footer */}
                      {(invoiceSettings?.payment_terms || invoiceSettings?.thank_you_message) && (
                        <div className="p-6 border-t border-gray-200">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {invoiceSettings?.payment_terms && (
                              <div>
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Terms & Conditions</h4>
                                <p className="text-sm text-gray-600">{invoiceSettings.payment_terms}</p>
                              </div>
                            )}
                            {invoiceSettings?.thank_you_message && (
                              <div className="sm:text-right">
                                <p className="text-sm font-medium" style={{ color: invoiceSettings?.primary_color || '#3B82F6' }}>
                                  {invoiceSettings.thank_you_message}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div> {/* Close main scrollable content div (flex-1 overflow-y-auto) */}
          </div> {/* Close inner container div (h-full flex flex-col) */}
        </div> {/* Close modal content div */}
      </div> {/* Close backdrop div */}

      {/* Convert Options Modal */}
      <ConvertModal
        isOpen={isConvertOptionsModalOpen}
        onClose={() => setIsConvertOptionsModalOpen(false)}
        onConvertToBooking={() => {
          if ((localProposal as any).booking_id) {
            handleConvertToBooking();
          } else {
            setIsConvertOptionsModalOpen(false);
            setIsConvertModalOpen(true);
          }
        }}
        onConvertToMobileBooking={() => {
          setIsConvertOptionsModalOpen(false);
          setIsConvertMobileModalOpen(true);
        }}
        onConvertToProject={handleConvertToProject}
        hasCalendarApp={hasCalendarApp}
        hasProjectsApp={hasProjectsApp}
        projectId={localProposal.project_id || null}
        bookingId={(localProposal as any).booking_id || null}
        showLocationBooking={showLocationBooking}
        showMobileBooking={showMobileBooking}
      />

      <CreateBookingModal
        isOpen={isConvertModalOpen}
        onClose={() => {
          setIsConvertModalOpen(false); 
        }}
        initialProposal={localProposal}
        onSuccess={async () => {
          setIsConvertModalOpen(false);
          
          // Refresh the proposal data to show the updated status
          if (proposal.id) {
            try {
              const updatedProposal = await getProposal(proposal.id);
              if (updatedProposal) {
                setLocalProposal(updatedProposal);
                onUpdate?.(updatedProposal);
              }
            } catch (error) {
              console.error('Error refreshing proposal data:', error);
            }
          }
          
          onClose();
        }}
      />

      <CreateMobileBookingModal
        isOpen={isConvertMobileModalOpen}
        onClose={() => {
          setIsConvertMobileModalOpen(false);
        }}
        initialProposal={localProposal}
        onSuccess={async () => {
          setIsConvertMobileModalOpen(false);
          
          if (proposal.id) {
            try {
              const updatedProposal = await getProposal(proposal.id);
              if (updatedProposal) {
                setLocalProposal(updatedProposal);
                onUpdate?.(updatedProposal);
              }
            } catch (error) {
              console.error('Error refreshing proposal data:', error);
            }
          }
          
          onClose();
        }}
      />

      {/* Email Modal */}
      {isEmailModalOpen && (
        <ProposalEmailModal
          isOpen={isEmailModalOpen}
          onClose={() => setIsEmailModalOpen(false)}
          onSend={handleEmailSend}
          proposalTitle={localProposal.title || 'New Proposal'}
          businessName={businessName}
          customerEmail={localProposal.customer?.email || ''}
          customerFirstName={localProposal.customer?.first_name || 'there'}
          proposalId={localProposal.id}
          businessId={localProposal.business_id}
        />
      )}

      {/* SMS Modal */}
      {isSMSModalOpen && (
        <ProposalSMSModal
          isOpen={isSMSModalOpen}
          onClose={() => setIsSMSModalOpen(false)}
          onSend={handleSMSSend}
          proposalTitle={localProposal.title || 'New Proposal'}
          businessName={businessName}
          customerPhone={localProposal.customer?.phone || ''}
          customerFirstName={localProposal.customer?.first_name || 'there'}
          proposalId={localProposal.id}
          businessId={localProposal.business_id}
        />
      )}

      {/* Customer Details Modal */}
      {fullCustomer && (
        <CustomerDetailsModal
          isOpen={isCustomerDetailsModalOpen}
          onClose={() => setIsCustomerDetailsModalOpen(false)}
          customer={fullCustomer}
          zIndex={100140}
          onUpdate={async () => {
            if (fullCustomer?.id) {
              try {
                const refreshed = await getCustomer(fullCustomer.id);
                if (refreshed) {
                  setFullCustomer(refreshed);
                  setLocalProposal({
                    ...localProposal,
                    customer: {
                      first_name: refreshed.first_name,
                      last_name: refreshed.last_name,
                      email: refreshed.email || '',
                      phone: refreshed.phone || ''
                    }
                  });
                }
              } catch (err) {
                console.error('Error refreshing customer after update:', err);
              }
            }
            if (onUpdate && localProposal.id) {
              try {
                const updatedProposal = await getProposal(localProposal.id);
                onUpdate(updatedProposal);
              } catch (err) {
                console.error('Error refreshing proposal after customer update:', err);
              }
            }
          }}
        />
      )}

      {/* Customer Message Modal */}
      {fullCustomer && (
        <Suspense fallback={null}>
          <CustomerMessageModal
            isOpen={isMessageModalOpen}
            onClose={() => setIsMessageModalOpen(false)}
            customer={fullCustomer}
            businessId={localProposal.business_id}
            initialComposerType={messageModalComposerType}
          />
        </Suspense>
      )}

      {/* Edit Proposal Modal */}
      <CreateProposalModal
        isOpen={isEditProposalModalOpen}
        onClose={() => setIsEditProposalModalOpen(false)}
        onSuccess={async (updatedProposal) => {
          setIsEditProposalModalOpen(false);
          setLocalProposal(updatedProposal);
          // Reload per-service photos + line_key maps so newly added/removed
          // images show immediately without reopening the modal.
          if (updatedProposal?.id) loadProposalPhotos(updatedProposal.id);
          if (onUpdate) {
            onUpdate(updatedProposal);
          }
          toast.success('Proposal updated successfully');
        }}
        initialProposal={localProposal}
        initialCustomer={fullCustomer || (localProposal.customer ? {
          id: localProposal.customer_id,
          first_name: localProposal.customer.first_name,
          last_name: localProposal.customer.last_name || '',
          email: localProposal.customer.email || '',
          phone: localProposal.customer.phone || ''
        } as Customer : undefined)}
        initialProjectId={localProposal.project_id || undefined}
        initialOpportunityId={localProposal.pipeline_opportunities_id || undefined}
      />

      {/* Record Payment Modal */}
      {selectedBusiness && localProposal?.id && (
        <RecordPaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          onSuccess={async (paymentId) => {
            // Refresh proposal data to show updated payment
            if (localProposal?.id) {
              try {
                const updatedProposal = await getProposal(localProposal.id);
                setLocalProposal(updatedProposal);
                if (onUpdate) {
                  onUpdate(updatedProposal);
                }
                // Refresh payment list and amount paid
                const { data: payments, error } = await supabase
                  .from('payments')
                  .select('*')
                  .eq('proposal_id', localProposal.id)
                  .order('created_at', { ascending: false });
                
                if (!error && payments) {
                  setProposalPayments(payments);
                  const completedPayments = payments.filter(p => p.status === 'completed');
                  const totalPaid = completedPayments.reduce((sum, payment) => sum + (payment.base_amount || payment.amount || 0), 0);
                  setProposalAmountPaid(totalPaid);
                }
              } catch (error) {
                console.error('Error refreshing proposal after payment:', error);
              }
            }
            setIsPaymentModalOpen(false);
          }}
          proposalId={localProposal.id}
          businessId={selectedBusiness.id}
          bookingTotal={localProposal.total_amount || 0}
          amountPaid={proposalAmountPaid}
          initialDepositAmount={calculatedDepositAmount}
          customerId={localProposal.customer_id || undefined}
        />
      )}
    </>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues that cause modal to render behind navigation
  return (
    <>
      {createPortal(modalContent, document.body)}
      {isLinkModalOpen && createPortal(
        <div 
          className="fixed inset-0 transition-opacity duration-200 bg-black/50"
          style={{ zIndex: resolvedZIndex + 5 }}
          onClick={() => {
            setIsLinkModalOpen(false);
            setLinkModalView('options');
            setProjectSearchQuery('');
            setBookingSearchQuery('');
          }}
        >
          <div 
            className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-2xl transform transition-transform duration-200 translate-x-0"
            onClick={e => e.stopPropagation()}
            style={{
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
              height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {linkModalView !== 'options' && (
                    <button
                      onClick={() => {
                        setLinkModalView('options');
                        setProjectSearchQuery('');
                        setOpportunitySearchQuery('');
                        setBookingSearchQuery('');
                      }}
                      className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                  )}
                  <h3 className="text-lg font-semibold text-gray-900">
                    {linkModalView === 'options' ? 'Link Proposal' : linkModalView === 'projects' ? 'Link Project' : linkModalView === 'bookings' ? 'Link Booking' : 'Link Opportunity'}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setIsLinkModalOpen(false);
                    setLinkModalView('options');
                    setProjectSearchQuery('');
                    setOpportunitySearchQuery('');
                    setBookingSearchQuery('');
                  }}
                  className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {linkModalView === 'options' ? (
                  <div className="space-y-3">
                    {/* Projects Option */}
                    {hasProjectsApp && (
                      <button
                        type="button"
                        onClick={async () => {
                          setLinkModalView('projects');
                          await fetchProjects();
                        }}
                        className={`w-full px-4 py-3 text-left bg-white border rounded-lg hover:bg-gray-50 transition-all duration-150 ${
                          localProposal.project_id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            localProposal.project_id ? 'bg-blue-100' : 'bg-gray-100'
                          }`}>
                            <FileText className={`h-5 w-5 ${localProposal.project_id ? 'text-blue-600' : 'text-gray-600'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900">Projects</div>
                            {localProposal.project_id && project && (
                              <div className="text-xs text-blue-600 mt-0.5">Linked: {project.title}</div>
                            )}
                          </div>
                          {localProposal.project_id && (
                            <div className="ml-3 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                              <X className="h-3 w-3 text-white rotate-45" />
                            </div>
                          )}
                        </div>
                      </button>
                    )}
                    {/* Opportunities Option - Only show if CRM app is installed */}
                    {hasCRMApp && (
                      <button
                        type="button"
                        onClick={async () => {
                          setLinkModalView('opportunities');
                          await fetchOpportunities();
                        }}
                        className={`w-full px-4 py-3 text-left bg-white border rounded-lg hover:bg-gray-50 transition-all duration-150 ${
                          localProposal.pipeline_opportunities_id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            localProposal.pipeline_opportunities_id ? 'bg-blue-100' : 'bg-gray-100'
                          }`}>
                            <Users className={`h-5 w-5 ${localProposal.pipeline_opportunities_id ? 'text-blue-600' : 'text-gray-600'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900">Opportunities</div>
                            {localProposal.pipeline_opportunities_id && opportunity && (
                              <div className="text-xs text-blue-600 mt-0.5">Linked: {opportunity.title}</div>
                            )}
                          </div>
                          {localProposal.pipeline_opportunities_id && (
                            <div className="ml-3 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                              <X className="h-3 w-3 text-white rotate-45" />
                            </div>
                          )}
                        </div>
                      </button>
                    )}
                    {/* Bookings Option - Only show if Calendar app is installed */}
                    {hasCalendarApp && (
                      <button
                        type="button"
                        onClick={async () => {
                          setLinkModalView('bookings');
                          await fetchBookingsForLink();
                        }}
                        className={`w-full px-4 py-3 text-left bg-white border rounded-lg hover:bg-gray-50 transition-all duration-150 ${
                          (localProposal as any).booking_id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            (localProposal as any).booking_id ? 'bg-blue-100' : 'bg-gray-100'
                          }`}>
                            <Calendar className={`h-5 w-5 ${(localProposal as any).booking_id ? 'text-blue-600' : 'text-gray-600'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900">Bookings</div>
                            {(localProposal as any).booking_id && linkedBooking && (
                              <div className="text-xs text-blue-600 mt-0.5">Linked: {linkedBooking.title || `Booking #${linkedBooking.booking_number}`}</div>
                            )}
                          </div>
                          {(localProposal as any).booking_id && (
                            <div className="ml-3 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                              <X className="h-3 w-3 text-white rotate-45" />
                            </div>
                          )}
                        </div>
                      </button>
                    )}
                  </div>
                ) : linkModalView === 'projects' ? (
                  <>
                    {/* Unlink Option */}
                    {localProposal.project_id && (
                      <button
                        type="button"
                        onClick={() => handleProjectLinkChange(null)}
                        className="w-full px-4 py-3 text-left bg-white border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 active:bg-red-100 transition-all duration-150 text-red-600 mb-4"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                            <Link className="h-5 w-5 text-red-600 rotate-45" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold">Unlink Project</div>
                            <div className="text-xs text-red-500 mt-0.5">Remove the link to this project</div>
                          </div>
                        </div>
                      </button>
                    )}

                    {/* Search Input */}
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={projectSearchQuery}
                        onChange={(e) => setProjectSearchQuery(e.target.value)}
                        placeholder="Search projects..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>

                    {/* Projects List */}
                    {isLoadingProjects ? (
                      <div className="text-center py-8 text-gray-500 text-sm">Loading projects...</div>
                    ) : filteredProjects.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {projectSearchQuery ? 'No projects found' : 'No projects available'}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredProjects.map((proj, index) => {
                          const getStatusColor = (status: string) => {
                            switch (status) {
                              case 'Completed':
                              case 'Approved':
                                return 'bg-green-500';
                              case 'in progress':
                              case 'Scheduled':
                                return 'bg-blue-500';
                              case 'Pending':
                                return 'bg-yellow-500';
                              case 'draft':
                                return 'bg-gray-300';
                              default:
                                return 'bg-gray-400';
                            }
                          };
                          
                          const getStatusBadgeColor = (status: string) => {
                            switch (status) {
                              case 'Completed':
                              case 'Approved':
                                return 'bg-green-100 text-green-700';
                              case 'in progress':
                              case 'Scheduled':
                                return 'bg-blue-100 text-blue-700';
                              case 'Pending':
                                return 'bg-yellow-100 text-yellow-700';
                              case 'draft':
                                return 'bg-gray-100 text-gray-600';
                              default:
                                return 'bg-gray-100 text-gray-600';
                            }
                          };
                          
                          return (
                            <button
                              key={proj.id}
                              type="button"
                              onClick={() => handleProjectLinkChange(proj.id)}
                              className={`w-full bg-white rounded-xl border shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer flex overflow-hidden ${
                                localProposal.project_id === proj.id
                                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={{
                                animationDelay: `${index * 30}ms`
                              }}
                            >
                              <div className={`w-1 rounded-l-xl flex-shrink-0 ${getStatusColor(proj.status)}`} />
                              <div className="p-4 flex-1 flex items-center justify-between min-w-0 overflow-hidden">
                                <div className="flex items-center space-x-4 flex-1 min-w-0 overflow-hidden">
                                  <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                                    {proj.customer?.first_name?.[0] || ''}{proj.customer?.last_name?.[0] || ''}
                                  </div>
                                  <div className="flex-1 min-w-0 overflow-hidden">
                                    <div className="flex items-center space-x-3 mb-1 min-w-0">
                                      <h3 className="text-base font-semibold text-gray-900 truncate flex-shrink">
                                        {proj.customer?.first_name} {proj.customer?.last_name}
                                      </h3>
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${getStatusBadgeColor(proj.status)}`}>
                                        {proj.status ? proj.status.charAt(0).toUpperCase() + proj.status.slice(1) : 'Draft'}
                                      </span>
                                      {localProposal.project_id === proj.id && (
                                        <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                          <X className="h-3 w-3 text-white rotate-45" />
                                        </div>
                                      )}
                                    </div>
                                    <h2 className="text-base font-semibold text-gray-900 truncate">
                                      {proj.title}
                                    </h2>
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : linkModalView === 'opportunities' ? (
                  <>
                    {/* Unlink Option */}
                    {localProposal.pipeline_opportunities_id && (
                      <button
                        type="button"
                        onClick={() => handleOpportunityLinkChange(null)}
                        className="w-full px-4 py-3 text-left bg-white border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 active:bg-red-100 transition-all duration-150 text-red-600"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                            <Link className="h-5 w-5 text-red-600 rotate-45" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold">Unlink Opportunity</div>
                            <div className="text-xs text-red-500 mt-0.5">Remove the link to this opportunity</div>
                          </div>
                        </div>
                      </button>
                    )}

                    {/* Search Input */}
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={opportunitySearchQuery}
                        onChange={(e) => setOpportunitySearchQuery(e.target.value)}
                        placeholder="Search opportunities..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>

                    {/* Opportunities List */}
                    {isLoadingOpportunities ? (
                      <div className="text-center py-8 text-gray-500 text-sm">Loading opportunities...</div>
                    ) : filteredOpportunities.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {opportunitySearchQuery ? 'No opportunities found' : 'No opportunities available'}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredOpportunities.map((opp, index) => {
                          const getStatusColor = (status: string) => {
                            switch (status) {
                              case 'Won':
                                return 'bg-green-500';
                              case 'Open':
                                return 'bg-blue-500';
                              case 'Lost':
                                return 'bg-red-500';
                              default:
                                return 'bg-gray-400';
                            }
                          };
                          
                          const getStatusBadgeColor = (status: string) => {
                            switch (status) {
                              case 'Won':
                                return 'bg-green-100 text-green-700';
                              case 'Open':
                                return 'bg-blue-100 text-blue-700';
                              case 'Lost':
                                return 'bg-red-100 text-red-700';
                              default:
                                return 'bg-gray-100 text-gray-600';
                            }
                          };
                          
                          return (
                            <button
                              key={opp.id}
                              type="button"
                              onClick={() => handleOpportunityLinkChange(opp.id)}
                              className={`w-full bg-white rounded-xl border shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer flex overflow-hidden ${
                                localProposal.pipeline_opportunities_id === opp.id
                                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={{
                                animationDelay: `${index * 30}ms`
                              }}
                            >
                              <div className={`w-1 rounded-l-xl flex-shrink-0 ${getStatusColor(opp.status || 'Open')}`} />
                              <div className="p-4 flex-1 flex items-center justify-between min-w-0 overflow-hidden">
                                <div className="flex items-center space-x-4 flex-1 min-w-0 overflow-hidden">
                                  <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                                    {opp.customer?.first_name?.[0] || ''}{opp.customer?.last_name?.[0] || ''}
                                  </div>
                                  <div className="flex-1 min-w-0 overflow-hidden">
                                    <div className="flex items-center space-x-3 mb-1 min-w-0">
                                      <h3 className="text-base font-semibold text-gray-900 truncate flex-shrink">
                                        {opp.customer?.first_name} {opp.customer?.last_name}
                                      </h3>
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${getStatusBadgeColor(opp.status || 'Open')}`}>
                                        {opp.status || 'Open'}
                                      </span>
                                      {localProposal.pipeline_opportunities_id === opp.id && (
                                        <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                          <X className="h-3 w-3 text-white rotate-45" />
                                        </div>
                                      )}
                                    </div>
                                    <h2 className="text-base font-semibold text-gray-900 truncate">
                                      {opp.title}
                                    </h2>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-6 flex-shrink-0 ml-4">
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-xs text-gray-500 whitespace-nowrap">Value</p>
                                    <p className="text-lg font-bold text-gray-900 whitespace-nowrap">
                                      ${(opp.value || 0).toLocaleString()}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : linkModalView === 'bookings' ? (
                  <>
                    {/* Unlink Option */}
                    {(localProposal as any).booking_id && (
                      <button
                        type="button"
                        onClick={() => handleBookingLinkChange(null)}
                        className="w-full px-4 py-3 text-left bg-white border border-red-300 rounded-lg hover:bg-red-50 hover:border-red-400 active:bg-red-100 transition-all duration-150 text-red-600 mb-4"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                            <Link className="h-5 w-5 text-red-600 rotate-45" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold">Unlink Booking</div>
                            <div className="text-xs text-red-500 mt-0.5">Remove the link to this booking</div>
                          </div>
                        </div>
                      </button>
                    )}

                    {/* Search Input */}
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={bookingSearchQuery}
                        onChange={(e) => setBookingSearchQuery(e.target.value)}
                        placeholder="Search bookings..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>

                    {/* Bookings List */}
                    {isLoadingBookings ? (
                      <div className="text-center py-8 text-gray-500 text-sm">Loading bookings...</div>
                    ) : filteredBookings.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {bookingSearchQuery ? 'No bookings found' : 'No bookings available'}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {filteredBookings.map((bk, index) => {
                          const getStatusColor = (status: string) => {
                            switch (status) {
                              case 'scheduled': return 'bg-blue-500';
                              case 'in_progress': return 'bg-yellow-500';
                              case 'completed': return 'bg-green-500';
                              case 'cancelled': return 'bg-red-500';
                              default: return 'bg-gray-400';
                            }
                          };

                          const getStatusBadgeColor = (status: string) => {
                            switch (status) {
                              case 'scheduled': return 'bg-blue-100 text-blue-700';
                              case 'in_progress': return 'bg-yellow-100 text-yellow-700';
                              case 'completed': return 'bg-green-100 text-green-700';
                              case 'cancelled': return 'bg-red-100 text-red-700';
                              default: return 'bg-gray-100 text-gray-600';
                            }
                          };

                          return (
                            <button
                              key={bk.id}
                              type="button"
                              onClick={() => handleBookingLinkChange(bk.id)}
                              className={`w-full bg-white rounded-xl border shadow-sm hover:shadow-lg transition-all duration-200 cursor-pointer flex overflow-hidden ${
                                (localProposal as any).booking_id === bk.id
                                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={{ animationDelay: `${index * 30}ms` }}
                            >
                              <div className={`w-1 rounded-l-xl flex-shrink-0 ${getStatusColor(bk.status || 'pending')}`} />
                              <div className="p-4 flex-1 flex items-center justify-between min-w-0 overflow-hidden">
                                <div className="flex items-center space-x-4 flex-1 min-w-0 overflow-hidden">
                                  <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                                    {bk.customer?.first_name?.[0] || ''}{bk.customer?.last_name?.[0] || ''}
                                  </div>
                                  <div className="flex-1 min-w-0 overflow-hidden">
                                    <div className="flex items-center space-x-3 mb-1 min-w-0">
                                      <h3 className="text-base font-semibold text-gray-900 truncate flex-shrink">
                                        {bk.customer?.first_name} {bk.customer?.last_name}
                                      </h3>
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full flex-shrink-0 ${getStatusBadgeColor(bk.status || 'pending')}`}>
                                        {(bk.status || 'pending').replace('_', ' ')}
                                      </span>
                                      {(localProposal as any).booking_id === bk.id && (
                                        <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                          <X className="h-3 w-3 text-white rotate-45" />
                                        </div>
                                      )}
                                    </div>
                                    <h2 className="text-base font-semibold text-gray-900 truncate">
                                      {bk.title || `Booking #${bk.booking_number}`}
                                    </h2>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-6 flex-shrink-0 ml-4">
                                  <div className="text-right flex-shrink-0">
                                    <p className="text-xs text-gray-500 whitespace-nowrap">Booking #</p>
                                    <p className="text-lg font-bold text-gray-900 whitespace-nowrap">
                                      {bk.booking_number}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}